import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  useTheme,
  alpha,
  Stack,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Snackbar,
  Tooltip,
  Switch,
  Collapse,
} from '@mui/material';
import {
  Science,
  Analytics,
  CheckCircle,
  Assignment,
  Timer,
  PriorityHigh,
  TrendingUp,
  Assessment,
  Warning,
  Settings,
  BarChart,
  Timeline,
  Tune,
  Add,
  Delete,
  Save,
  PlayArrow,
  Stop,
  Refresh,
  CloudUpload,
  GetApp,
  Error,
  Send,
  Visibility,
  Edit,
  FileUpload,
  AutoAwesome,
  TipsAndUpdates,
  ExpandMore,
  Close,
  ExpandLess,
  ContentCopy,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import apiService from '../../services/api';

// Enhanced interfaces for comprehensive SAA analysis
interface PreparationData {
  initial_weight: number;
  digestion_method: string;
  initial_volume: number;
  dilutions: Array<{
    volumeTaken: number;
    finalVolume: number;
    dilutionFactor: number;
  }>;
  total_dilution_factor: number;
  preparation_completed_at: string;
  technician_id: string;
  technician_name?: string;
  // NEW: Additional dilution fields for choice 2
  vf1?: number;
  vp1?: number;
}

interface CalibrationStandard {
  id: string;
  element: string;
  concentration_ppm: number;
  absorbance: number;
  used_at: string;
}

interface QualityControl {
  id: string;
  type: 'BLANK' | 'STANDARD' | 'DUPLICATE' | 'SPIKE';
  element: string;
  expected_value?: number;
  measured_value: number;
  absorbance: number;
  recovery_percent?: number;
  within_limits: boolean;
  timestamp: string;
}

interface AnalysisReading {
  id: string;
  element: string;
  absorbance: number;
  concentration_ppm: number;
  concentration_final_percent: number;
  uncertainty: number;
  timestamp: string;
  instrument_settings?: {
    wavelength: number;
    slit_width: number;
    flame_type: string;
    lamp_current: number;
  };
}

interface InstrumentStatus {
  last_calibration: string;
  r_squared: number;
  lamp_hours: number;
  flame_stable: boolean;
  background_correction: boolean;
  status: 'READY' | 'CALIBRATING' | 'MAINTENANCE' | 'ERROR';
  last_qc_check: string;
}

interface ControlChartData {
  element: string;
  measurements: Array<{
    date: string;
  value: number;
    control_limit_upper: number;
    control_limit_lower: number;
    warning_limit_upper: number;
    warning_limit_lower: number;
    out_of_control: boolean;
  }>;
}

interface SAAAnalysisForm {
  sample_id: string;
  preparation_data: PreparationData | null;
  calibration_standards: CalibrationStandard[];
  quality_controls: QualityControl[];
  analysis_readings: AnalysisReading[];
  instrument_status: InstrumentStatus;
  analysis_started_at: string | null;
  analysis_completed_at: string | null;
  analyst_id: string;
  notes: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'VALIDATED';
}

// New interfaces for CSV/Excel import
interface ImportedResultRow {
  sample_id?: string;
  lab_ref?: string;
  customer_ref?: string;
  lab_weight?: number;
  dilution_factor?: number;
  [element: string]: any; // Dynamic element concentrations
}

interface ImportValidationResult {
  isValid: boolean;
  validRows: ImportedResultRow[];
  invalidRows: { row: number; data: ImportedResultRow; errors: string[] }[];
  detectedElements: string[];
  totalRows: number;
}

interface FileImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (results: ImportedResultRow[]) => void;
  availableElements: string[];
}

interface MethodValidation {
  element: string;
  lod_ppm: number;
  loq_ppm: number;
  linear_range_min_ppm: number;
  linear_range_max_ppm: number;
  accuracy_percent: number;
  precision_rsd_percent: number;
  recovery_min_percent: number;
  recovery_max_percent: number;
  expanded_uncertainty_percent: number;
  validation_date: string;
}

interface SAAFileInfo {
  filename: string;
  path: string;
  size: number;
  modified: string;
  matchedSamples: string[];
}

interface BatchElementData {
  ppm: number;
  timestamp: string;
  originalReading?: number; // Original L value from instrument
  calculatedFromFormula?: boolean; // Indicates if L × V/p × vf/vp was applied
}

interface BatchAnalysisData {
  [sampleId: string]: {
    [element: string]: BatchElementData;
  };
}

// QC Data interfaces for SAA import
interface QCReading {
  element: string;
  name: string;
  reading: number;
  unit: string;
  type: string; // TQ, BLANK, CAL_ZERO, STANDARD
}

interface QCElementSummary {
  tq_count: number;
  tq_average: number;
  tq_rsd: number;
  blank_count: number;
  blank_average: number;
  cal_zero_count: number;
  standard_count: number;
}

interface QCData {
  qcReadings: QCReading[];
  summary: Record<string, QCElementSummary>;
  filename?: string;
}

interface QCAlert {
  element: string;
  type: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO' | 'OK';
  message: string;
  value?: number;
  threshold?: number;
}

// CSV/Excel Import Dialog Component
const FileImportDialog: React.FC<FileImportDialogProps> = ({
  open,
  onClose,
  onImport,
  availableElements
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null);
  const [previewData, setPreviewData] = useState<ImportedResultRow[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setValidationResult(null);
    setPreviewData([]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type.includes('csv') || droppedFile.type.includes('excel') || droppedFile.type.includes('sheet'))) {
      handleFileSelect(droppedFile);
    }
  };

  const parseCSV = (text: string): ImportedResultRow[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows: ImportedResultRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const row: ImportedResultRow = {};
      
      headers.forEach((header, index) => {
        const value = values[index]?.trim();
        if (value) {
          // Map common column names
          if (header.includes('sample') || header.includes('id')) {
            row.sample_id = value;
          } else if (header.includes('lab') && header.includes('ref')) {
            row.lab_ref = value;
          } else if (header.includes('customer') || header.includes('client')) {
            row.customer_ref = value;
          } else if (header.includes('weight')) {
            row.lab_weight = parseFloat(value) || 0;
          } else if (header.includes('dilution')) {
            row.dilution_factor = parseFloat(value) || 1;
          } else {
            // Check if it's an element symbol
            const elementSymbol = header.toUpperCase();
            if (availableElements.includes(elementSymbol)) {
              row[elementSymbol] = parseFloat(value) || 0;
            }
          }
        }
      });
      
      if (Object.keys(row).length > 0) {
        rows.push(row);
      }
    }

    return rows;
  };

  const validateImportData = (data: ImportedResultRow[]): ImportValidationResult => {
    const validRows: ImportedResultRow[] = [];
    const invalidRows: { row: number; data: ImportedResultRow; errors: string[] }[] = [];
    const detectedElements = new Set<string>();

    data.forEach((row, index) => {
      const errors: string[] = [];

      // Validate required fields
      if (!row.sample_id && !row.lab_ref) {
        errors.push('Sample ID ou Lab Reference requis');
      }

      if (!row.lab_weight || row.lab_weight <= 0) {
        errors.push('Poids laboratoire requis et doit être > 0');
      }

      if (!row.dilution_factor || row.dilution_factor <= 0) {
        errors.push('Facteur de dilution requis et doit être > 0');
      }

      // Check for element concentrations
      const elementValues = Object.keys(row).filter(key => 
        availableElements.includes(key) && typeof row[key] === 'number'
      );

      if (elementValues.length === 0) {
        errors.push('Aucune concentration d\'élément détectée');
      }

      elementValues.forEach(element => detectedElements.add(element));

      if (errors.length === 0) {
        validRows.push(row);
      } else {
        invalidRows.push({ row: index + 1, data: row, errors });
      }
    });

    return {
      isValid: invalidRows.length === 0,
      validRows,
      invalidRows,
      detectedElements: Array.from(detectedElements),
      totalRows: data.length
    };
  };

  const handlePreviewFile = async () => {
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const parsedData = parseCSV(text);
      const validation = validateImportData(parsedData);
      
      setValidationResult(validation);
      setPreviewData(parsedData.slice(0, 5)); // Show first 5 rows for preview
    } catch (error) {
      console.error('Error parsing file:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = () => {
    if (validationResult?.validRows) {
      onImport(validationResult.validRows);
      onClose();
    }
  };

  const handleClose = () => {
    setFile(null);
    setValidationResult(null);
    setPreviewData([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <FileUpload />
          <Typography variant="h6">Import des Résultats Analytiques</Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Format supporté:</strong> CSV avec colonnes: Sample_ID, Lab_Ref, Customer_Ref, Lab_Weight, Dilution_Factor, et les éléments ({availableElements.join(', ')})
            </Typography>
          </Alert>

          {/* File Upload Area */}
          <Paper
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.300',
              backgroundColor: dragOver ? 'primary.light' : 'grey.50',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) handleFileSelect(selectedFile);
              }}
            />
            
            <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {file ? file.name : 'Glissez votre fichier ici ou cliquez pour sélectionner'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Formats supportés: CSV, Excel (.xlsx, .xls)
            </Typography>
          </Paper>

          {/* Preview Button */}
          {file && !validationResult && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={handlePreviewFile}
                disabled={importing}
                startIcon={importing ? <CircularProgress size={20} /> : <Visibility />}
              >
                {importing ? 'Analyse en cours...' : 'Prévisualiser le fichier'}
              </Button>
            </Box>
          )}

          {/* Validation Results */}
          {validationResult && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Résultats de la validation
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {validationResult.totalRows}
                    </Typography>
                    <Typography variant="body2">Total lignes</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {validationResult.validRows.length}
                    </Typography>
                    <Typography variant="body2">Valides</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {validationResult.invalidRows.length}
                    </Typography>
                    <Typography variant="body2">Erreurs</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {validationResult.detectedElements.length}
                    </Typography>
                    <Typography variant="body2">Éléments</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Detected Elements */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Éléments détectés:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {validationResult.detectedElements.map(element => (
                    <Chip key={element} label={element} color="primary" size="small" />
                  ))}
                </Stack>
              </Box>

              {/* Preview Table */}
              {previewData.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">
                      Aperçu des données (5 premières lignes)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Sample ID</TableCell>
                            <TableCell>Lab Ref</TableCell>
                            <TableCell>Weight (g)</TableCell>
                            <TableCell>Dilution</TableCell>
                            {validationResult.detectedElements.map(element => (
                              <TableCell key={element}>{element}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>{row.sample_id || row.lab_ref}</TableCell>
                              <TableCell>{row.lab_ref}</TableCell>
                              <TableCell>{row.lab_weight}</TableCell>
                              <TableCell>{row.dilution_factor}</TableCell>
                              {validationResult.detectedElements.map(element => (
                                <TableCell key={element}>
                                  {row[element] !== undefined ? row[element] : '-'}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Error Details */}
              {validationResult.invalidRows.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" color="error">
                      Erreurs de validation ({validationResult.invalidRows.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {validationResult.invalidRows.map((invalidRow, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Error color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Ligne ${invalidRow.row}`}
                            secondary={invalidRow.errors.join(', ')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Annuler</Button>
        {validationResult?.isValid && (
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            startIcon={<CheckCircle />}
          >
            Importer {validationResult.validRows.length} résultats
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// 🚀 PERFORMANCE OPTIMIZED: Native input component (10x faster than Material-UI)
const NativeNumberInput = memo(({ 
  value, 
  onChange, 
  sampleId, 
  element,
  hasValue 
}: {
  value: number;
  onChange: (sampleId: string, element: string, value: number) => void;
  sampleId: string;
  element: string;
  hasValue: boolean;
}) => (
  <input
    type="number"
    step="0.001"
    min="0"
    value={value || ''}
    onChange={(e) => {
      const newValue = parseFloat(e.target.value) || 0;
      onChange(sampleId, element, newValue);
    }}
    style={{
      width: '70px',
      height: '28px',
      border: hasValue ? '2px solid #4caf50' : '1px solid #ddd',
      borderRadius: '4px',
      padding: '2px 4px',
      fontSize: '0.75rem',
      textAlign: 'center' as const,
      backgroundColor: hasValue ? '#e8f5e8' : 'transparent',
      outline: 'none',
      boxSizing: 'border-box' as const
    }}
    onFocus={(e) => e.target.select()}
    placeholder="0.0"
  />
));

const SAAAnalystDashboard: React.FC = memo(() => {
  const { user } = useAuth();
  const theme = useTheme();
  
  // Main state
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzingBatch, setAnalyzingBatch] = useState(false); // 🚀 NEW: Button loading state
  const [sendingToPrepBatch, setSendingToPrepBatch] = useState(false); // 🔥 NEW: Send to prep loading state
  const [error, setError] = useState<string | null>(null);
  
  // Analysis dialog state
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [analysisDialog, setAnalysisDialog] = useState(false);
  const [batchAnalysisDialog, setBatchAnalysisDialog] = useState(false);
  const [analysisForm, setAnalysisForm] = useState<SAAAnalysisForm | null>(null);
  const [batchAnalysisData, setBatchAnalysisData] = useState<BatchAnalysisData>({});
  // 🔥 FIXED: Changed from sample-level to element-level dilution choices
  const [elementDilutionChoices, setElementDilutionChoices] = useState<Record<string, Record<string, 0 | 1 | 2>>>({});
  
  // 🔥 NEW: Element-level dilution parameters (vf, vp applied per element across ALL samples)
  // Structure: { [element]: { vf, vp, vf1, vp1 } }
  const [elementDilutionParams, setElementDilutionParams] = useState<Record<string, {
    vf: number;   // Volume final (ml) - same for all samples for this element
    vp: number;   // Volume prélevé (ml) - same for all samples for this element
    vf1: number;  // Alternative vf1 for dilution choice 2
    vp1: number;  // Alternative vp1 for dilution choice 2
  }>>({});
  
  const [currentTab, setCurrentTab] = useState(0);
  
  // Instrument and control chart state
  const [instrumentStatus, setInstrumentStatus] = useState<InstrumentStatus>({
    last_calibration: new Date().toISOString(),
    r_squared: 0.9987,
    lamp_hours: 234,
    flame_stable: true,
    background_correction: true,
    status: 'READY',
    last_qc_check: new Date().toISOString(),
  });
  
  const [controlCharts, setControlCharts] = useState<ControlChartData[]>([]);
  const [outOfLimitsSamples, setOutOfLimitsSamples] = useState<Sample[]>([]);
  
  // Statistics
  const [stats, setStats] = useState({
    ready_for_analysis: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
    instrument_uptime: 98.5,
    average_r_squared: 0.9987,
  });

  // Available elements for SAA analysis
  const saaElements = [
    { symbol: 'Au', name: 'Or', wavelength: 242.8, typical_range: '0.01-100 ppm' },
    { symbol: 'Ag', name: 'Argent', wavelength: 328.1, typical_range: '0.1-100 ppm' },
    { symbol: 'Cu', name: 'Cuivre', wavelength: 324.8, typical_range: '0.5-100 ppm' },
    { symbol: 'Pb', name: 'Plomb', wavelength: 283.3, typical_range: '1-100 ppm' },
    { symbol: 'Zn', name: 'Zinc', wavelength: 213.9, typical_range: '0.1-50 ppm' },
    { symbol: 'Fe', name: 'Fer', wavelength: 248.3, typical_range: '1-100 ppm' },
    { symbol: 'Al', name: 'Aluminium', wavelength: 309.3, typical_range: '5-500 ppm' },
    { symbol: 'Mg', name: 'Magnésium', wavelength: 285.2, typical_range: '0.1-50 ppm' },
    { symbol: 'Ca', name: 'Calcium', wavelength: 422.7, typical_range: '1-100 ppm' },
    { symbol: 'Mn', name: 'Manganèse', wavelength: 279.5, typical_range: '0.5-100 ppm' },
  ];

  // New state for import functionality
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importedResults, setImportedResults] = useState<ImportedResultRow[]>([]);
  
  // SAA Directory Scanning State
  const [importLoading, setImportLoading] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<SAAFileInfo[]>([]);
  const [fileImportDialogOpen, setFileImportDialogOpen] = useState(false);
  
  // Manual entry dialog state
  const [editingReading, setEditingReading] = useState<AnalysisReading | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // QC Data state - for displaying QC alerts from CSV import
  const [qcData, setQcData] = useState<QCData | null>(null);
  const [qcAlerts, setQcAlerts] = useState<QCAlert[]>([]);

  // Available SAA elements (from your database)
  const availableSAAElements = ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];

  // Method validation data
  const [methodValidations, setMethodValidations] = useState<MethodValidation[]>([]);

  // Batch analysis common values - remove complex calibration since we work with ppm directly
  const [batchCommonValues, setBatchCommonValues] = useState({
    // Default values for reference - not used in calculations since we input ppm directly
    referenceStandard: 'Certified Reference Material',
    detectionMethod: 'Atomic Absorption Spectroscopy'
  });

  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());

  useEffect(() => {
    // 🚀 OPTIMIZED: Load critical data first, then secondary data
    const loadCriticalData = async () => {
      await loadSAAAnalysisData(); // Most important - load samples first
      await loadInstrumentStatus(); // Then instrument status
    };
    
    const loadSecondaryData = async () => {
      await loadControlCharts();
      await loadPendingSamples();
      await loadPreparationData();
      await loadMethodValidations();
    };
    
    // Load critical data immediately
    loadCriticalData();
    
    // Load secondary data after a short delay to reduce initial load
    setTimeout(loadSecondaryData, 500);
    
    // 🚀 OPTIMIZED: Reduced interval frequency to reduce scroll lag
    const interval = setInterval(() => {
      loadPendingSamples();
    }, 30000); // 30 seconds instead of 10 for better performance
    
    return () => clearInterval(interval);
  }, []);

  // Real-time calculation function that responds to dilution choice changes
  // 🔥 UPDATED: Now uses element-level dilution params (vf/vp per element, same for all samples)
  const calculateFromLecture = useCallback((
    lectureValue: number, // Raw "L" value from instrument
    sampleId: string,
    element: string, // 🔥 FIXED: Added element parameter for element-specific dilution
    prepData: any
  ): { ppm: number; percent: number } => {
    if (lectureValue <= 0 || !prepData) {
      return { ppm: 0, percent: 0 }; // Fallback
    }

    // 🔥 FIXED: Get dilution choice for this specific element, not the whole sample
    const elementDilutionChoice = elementDilutionChoices[sampleId]?.[element] ?? 1;
    
    // V and p come from sample prep data (sample-specific)
    const V = prepData.initial_volume || prepData.initialVolume || 1; // Volume initial
    const p = prepData.initial_weight || prepData.initialWeight || 0.5; // Poids
    
    // 🔥 NEW: vf/vp come from element-level params (same for all samples for this element)
    // Fall back to sample prep data if element params not set
    const elemParams = elementDilutionParams[element];
    const vf = elemParams?.vf ?? prepData.dilutions?.[0]?.finalVolume ?? 1; // Volume final (element-level)
    const vp = elemParams?.vp ?? prepData.dilutions?.[0]?.volumeTaken ?? 1; // Volume prélevé (element-level)
    const vf1 = elemParams?.vf1 ?? prepData.vf1 ?? 1; // Volume final alternatif (element-level)
    const vp1 = elemParams?.vp1 ?? prepData.vp1 ?? 1; // Volume prélevé alternatif (element-level)

    let ppm = 0;
    if (elementDilutionChoice === 0) {
      // Simplified formula: ppm = L × (V/p)
      ppm = (lectureValue * V) / p;
    } else if (elementDilutionChoice === 1) {
      // Complete formula: ppm = L × (V/p) × (vf/vp) - vf/vp from element params
      ppm = (lectureValue * V * vf) / (p * vp);
    } else if (elementDilutionChoice === 2) {
      // Alternative complete formula: ppm = L × (V/p) × (vf1/vp1) - vf1/vp1 from element params
      ppm = (lectureValue * V * vf1) / (p * vp1);
    }

    // 🔧 PRECISION FIX: Round to 6 decimal places to prevent floating point drift
    // This maintains analytical precision while eliminating JavaScript floating point errors
    ppm = Math.round(ppm * 1000000) / 1000000;

    // Convert ppm to percentage with same precision protection
    const percent = Math.round((ppm / 10000) * 1000000) / 1000000;

    return { ppm, percent };
  }, [elementDilutionChoices, elementDilutionParams]); // 🔥 UPDATED: Added elementDilutionParams dependency

  // Update stored ppm values when dilution choices OR element dilution params change
  // 🔥 UPDATED: Now also triggers when element-level vf/vp params change
  useEffect(() => {
    if (!selectedBatch) return;
    
    // 🔥 UPDATED: Iterate through ALL samples in batch (not just those with dilution choices)
    // This ensures recalculation when element params change
    selectedBatch.samples.forEach(sample => {
      const sampleId = sample.id;
      const prepData = sample.metadonnees?.saa_preparation;
      if (!prepData) return;
      
      const sampleAnalysisData = batchAnalysisData[sampleId] || {};
      // 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering
      const elementsToAnalyze = getSAAElementsFromMethod(sample);
      
      elementsToAnalyze.forEach(element => {
        const elementData = sampleAnalysisData[element];
        if (elementData?.originalReading > 0) {
          // 🔥 FIXED: Pass element parameter to calculation function
          const calculation = calculateFromLecture(elementData.originalReading, sampleId, element, prepData);
          updateBatchAnalysisData(sampleId, element, {
            ppm: calculation.ppm,
            timestamp: new Date().toISOString()
          });
        }
      });
    });
  }, [elementDilutionChoices, elementDilutionParams, selectedBatch, calculateFromLecture]); // 🔥 UPDATED: Added elementDilutionParams

  // Helper function to group samples by batch
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  // Check if sample is part of a batch
  const isBatchSample = (sample: Sample): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true;
  };

  // Get batch number for sample
  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  // Get batch position for sample
  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  // Get total samples in batch
  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  // Initialize batch analysis data
  const initializeBatchAnalysisData = (samples: Sample[]) => {
    const initialData: Record<string, Record<string, { ppm: number; timestamp: string }>> = {};
    
    samples.forEach(sample => {
      initialData[sample.id] = {};
      
      // 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering
      const elementsToAnalyze = getSAAElementsFromMethod(sample);
      elementsToAnalyze.forEach((element: string) => {
        initialData[sample.id][element] = {
          ppm: 0,
          timestamp: new Date().toISOString()
        };
      });
    });
    
    setBatchAnalysisData(initialData);
    // 🔧 Batch analysis data initialized
  };

  // Apply common settings to all batch samples (simplified since we work with ppm directly)
  const applyCommonSettingsToBatch = () => {
    // ⚡ Common settings applied to batch
    // The common values are stored in batchCommonValues state for reference only
    // Direct ppm input doesn't need calibration curve calculations
  };

  // Update individual sample analysis data in batch
  const updateBatchAnalysisData = (sampleId: string, element: string, value: { ppm: number; timestamp: string }) => {
    setBatchAnalysisData(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [element]: {
          ...prev[sampleId]?.[element], // Preserve existing data like originalReading, calculatedFromFormula
          ...value // Update with new ppm and timestamp
        }
      }
    }));
  };

  // 🚀 PERFORMANCE: Optimized change handler for native inputs
  const handleNativeInputChange = useCallback((sampleId: string, element: string, value: number) => {
    updateBatchAnalysisData(sampleId, element, {
      ppm: value,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Handle batch analysis start
  const handleStartBatchAnalysis = (batchNumber: string, batchSamples: Sample[]) => {
    // 🔬 Starting batch analysis
    setSelectedBatch({ batchNumber, samples: batchSamples });
    setBatchAnalysisDialog(true);
    
    // 🔬 Clear previous QC data
    setQcData(null);
    setQcAlerts([]);
    
    // Initialize batch analysis data
    initializeBatchAnalysisData(batchSamples);
    
    // 🔥 NEW: Initialize element-level dilution parameters from first sample's prep data
    if (batchSamples.length > 0) {
      const firstSample = batchSamples[0];
      const prepData = firstSample.metadonnees?.saa_preparation;
      const elementsToAnalyze = getSAAElementsFromMethod(firstSample);
      
      // Get default values from first sample's prep data
      const defaultVf = prepData?.dilutions?.[0]?.finalVolume || prepData?.finalVolume || 50;
      const defaultVp = prepData?.dilutions?.[0]?.volumeTaken || prepData?.volumeTaken || 10;
      const defaultVf1 = prepData?.vf1 || 25;
      const defaultVp1 = prepData?.vp1 || 5;
      
      // Initialize params for each element with same defaults
      const initialParams: Record<string, { vf: number; vp: number; vf1: number; vp1: number }> = {};
      elementsToAnalyze.forEach((element: string) => {
        initialParams[element] = {
          vf: defaultVf,
          vp: defaultVp,
          vf1: defaultVf1,
          vp1: defaultVp1
        };
      });
      
      setElementDilutionParams(initialParams);
      console.log('🔥 Initialized element dilution params:', initialParams);
    }
  };

  // 🔥 NEW: Function to send batch back to preparation chimique
  const handleSendBatchToPrepChimique = async (batchNumber: string, batchSamples: Sample[]) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir renvoyer le lot ${batchNumber} vers la préparation chimique SAA ? Cette action annulera toute donnée d'analyse en cours.`)) {
      return;
    }

    try {
      setSendingToPrepBatch(true);
      
      // 🔄 Sending batch back to prep chimique
      
      // Update each sample in the batch
      for (const sample of batchSamples) {
        await samplesService.updateSampleStatus(
          sample.id,
          SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE,
          `Renvoyé vers préparation chimique SAA depuis analyse - Lot: ${batchNumber} - Raison: Correction nécessaire en préparation`
        );
        
        // Clear any analysis data that might have been started
        await samplesService.updateSample(sample.id, {
          metadonnees: {
            ...sample.metadonnees,
            saa_analysis_form: null, // Clear analysis form
            saa_analysis_started_at: null, // Clear analysis start time
            analysis_status: null, // Reset analysis status
            saa_preparation_completed: false, // 🔧 FIX: Reset prep flag so it shows in Prep Chimique dashboard
            saa_analysis_completed: false, // 🔧 FIX: Reset analysis completed flag
            // Keep preparation data intact for reference
          }
        });
      }
      
      // Refresh the samples list
      await loadSAAAnalysisData();
      
              // ✅ Successfully sent batch back to prep chimique
      
      // Show success message
      setSnackbarMessage(`Lot ${batchNumber} renvoyé vers la préparation chimique SAA avec succès`);
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('❌ Error sending batch to prep chimique:', error);
      setSnackbarMessage(`Erreur lors du renvoi du lot vers la préparation chimique: ${error.message || 'Erreur inconnue'}`);
      setSnackbarOpen(true);
    } finally {
      setSendingToPrepBatch(false);
    }
  };

  // Save batch analysis and move all samples to validation
  const handleCompleteBatchAnalysis = async () => {
    if (!selectedBatch) return;

    try {
      setLoading(true);
      
      // 🚀 PERFORMANCE OPTIMIZATION: Use bulk operations instead of individual calls
      // 🔄 Using bulk analysis completion
      
      // Prepare bulk metadata update
      const bulkAnalysisData: Record<string, any> = {};
      
      // Process each sample in the batch
      selectedBatch.samples.forEach(sample => {
        const sampleAnalysisData = batchAnalysisData[sample.id] || {};
        const prepData = sample.metadonnees?.saa_preparation;
        
        // Get elements that were supposed to be analyzed (use method-based determination)
        const elementsToAnalyze = getSAAElementsFromMethod(sample);
        
        console.log(`🔍 Sample ${sample.sample_id}:`, {
          selected_elements: sample.metadonnees?.selected_elements,
          elementsToAnalyze,
          sampleAnalysisData: Object.keys(sampleAnalysisData),
          sampleAnalysisDataFull: sampleAnalysisData
        });
        
        // Construct analysis readings from batch data
        const analysisReadings: AnalysisReading[] = [];
        
        elementsToAnalyze.forEach((element: string) => {
          const elementData = sampleAnalysisData[element];
          if (elementData && typeof elementData.ppm === 'number') {
            // Validate against method limits
            const validation = methodValidations.find(v => v.element === element);
            const isValidReading = !validation || elementData.ppm >= validation.lod_ppm;
            
            // Calculate percentage from ppm
            const concentrationPercent = elementData.ppm / 10000; // Convert ppm to %
            
            analysisReadings.push({
              id: `${sample.id}-${element}-${Date.now()}`,
              element: element,
              absorbance: elementData.originalReading || 0, // ✅ Store original lecture (Reading) from CSV
              concentration_ppm: elementData.ppm,
              concentration_final_percent: concentrationPercent,
              uncertainty: validation?.expanded_uncertainty_percent || 5.0,
              timestamp: elementData.timestamp || new Date().toISOString(),
              instrument_settings: {
                wavelength: getElementWavelength(element),
                slit_width: 0.2,
                flame_type: 'Air-Acetylene',
                lamp_current: 10
              },


            });
          }
        });

        // Create SAA analysis form
        const completedForm = {
          sample_id: sample.sample_id,
          preparation_data: prepData,
          calibration_standards: [
            {
              standard_name: batchCommonValues.referenceStandard || 'Certified Reference Material',
              r_squared: 0.9998,
              linearity_range: '0-100 ppm',
              calibration_date: new Date().toISOString()
            }
          ],
          quality_controls: [
            {
              control_type: 'BLANK',
              control_id: 'METHOD_BLANK',
              measured_value: 0.001,
              acceptance_criteria: '< 0.005 ppm',
              status: 'PASS'
            }
          ],
          analysis_readings: analysisReadings,
          status: 'COMPLETED',
          analyst_signature: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
          analysis_completed_at: new Date().toISOString(),
          batch_analysis: true,
          batch_number: selectedBatch.batchNumber,
          detection_method: batchCommonValues.detectionMethod || 'Atomic Absorption Spectroscopy',
          reference_standard: batchCommonValues.referenceStandard || 'Certified Reference Material'
        };

        // Add to bulk data
        bulkAnalysisData[sample.id] = {
            saa_analysis_form: completedForm,
            // 🔧 CRITICAL FIX: MERGE with existing AU/ICP data instead of overwriting
            analysis_results: [
              // Preserve existing AU/ICP data (filter out SAA elements to avoid duplicates)
              ...(sample.metadonnees?.analysis_results || []).filter((r: any) => 
                !elementsToAnalyze.includes(r.element)
              ),
              // Add new SAA data
              ...analysisReadings.map(reading => ({
                element: reading.element,
                concentration_ppm: reading.concentration_ppm,
                concentration_percent: reading.concentration_final_percent,
                absorbance: 0, // Not used with direct ppm input
                detection_status: (() => {
                  const validation = methodValidations.find(v => v.element === reading.element);
                  if (!validation) return 'VALID';
                  if (reading.concentration_ppm < validation.lod_ppm) return 'BELOW_LOD';
                  if (reading.concentration_ppm < validation.loq_ppm) return 'BELOW_LOQ';
                  return 'VALID';
                })(),
                uncertainty: reading.uncertainty,
                timestamp: reading.timestamp
              }))
            ],
            saa_analysis_completed: true, // 🔥 CRITICAL FIX: Required for SAA Supervisor to detect completed samples
            saa_analysis_completed_at: new Date().toISOString(),
            saa_analyst_id: user?.id,
            saa_analyst_name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
            analysis_status: 'COMPLETED',
            batch_analysis: true,
            batch_analysis_summary: {
              batch_number: selectedBatch.batchNumber,
            is_batch_sample: true, // ✅ CRITICAL FIX: Required for supervisor dashboard to detect batch samples
              elements_analyzed: elementsToAnalyze, // ✅ Use ALL elements that were supposed to be analyzed, not just those with readings
              total_readings: analysisReadings.length,
              direct_ppm_input: true,
            // 🔥 FIXED: Store element-specific dilution choices instead of sample-level
            element_dilution_choices: elementDilutionChoices[sample.id] || {},
            dilution_choice: 1, // Default for backward compatibility
            dilution_formula: 'Element-specific choices stored in element_dilution_choices',
              reference_standard: batchCommonValues.referenceStandard,
              detection_method: batchCommonValues.detectionMethod,
              completion_date: new Date().toISOString(),
              below_lod_count: analysisReadings.filter(r => {
                const validation = methodValidations.find(v => v.element === r.element);
                return validation ? r.concentration_ppm < validation.lod_ppm : false;
              }).length
            }
        };
      });

      // Single bulk API call for all samples
      const result = await samplesService.completeBatchAnalysis(
        selectedBatch.samples,
        bulkAnalysisData,
          SampleStatus.VALIDATION_SERVICE,
        `Analyse SAA LOT terminée - Direct ppm input - Batch: ${selectedBatch.batchNumber}, ${selectedBatch.samples.length} échantillons`
        );

      if (result.errors.length > 0) {
        console.warn(`⚠️ Some errors during bulk analysis completion:`, result.errors);
        // Still continue if most samples were successful
      }

      console.log(`✅ Batch ${selectedBatch.batchNumber} analysis completed - ${result.updated} samples moved to validation`);
      
      // 🔬 Store QC data for supervisor review
      if (qcData && qcData.summary && Object.keys(qcData.summary).length > 0) {
        try {
          const token = localStorage.getItem('access_token');
          console.log(`🔬 Storing QC data for batch ${selectedBatch.batchNumber}:`, {
            elements: Object.keys(qcData.summary),
            alertsCount: qcAlerts.length,
            filename: qcData.filename
          });
          
          const qcStoreResponse = await fetch(`/api/v1/saa-qc/batch/${encodeURIComponent(selectedBatch.batchNumber)}/store`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              qcSummary: qcData.summary,
              alerts: qcAlerts,
              filename: qcData.filename || 'import'
            })
          });
          
          if (qcStoreResponse.ok) {
            const responseData = await qcStoreResponse.json();
            console.log(`✅ QC data stored successfully for batch ${selectedBatch.batchNumber}:`, responseData);
          } else {
            const errorText = await qcStoreResponse.text();
            console.error(`❌ Failed to store QC data for batch ${selectedBatch.batchNumber}: ${qcStoreResponse.status} - ${errorText}`);
          }
        } catch (qcError) {
          console.error(`❌ Error storing QC data:`, qcError);
          // Don't fail the entire batch submission for QC storage failure
        }
      } else {
        console.log(`ℹ️ No QC data to store for batch ${selectedBatch.batchNumber}`, { 
          qcDataExists: !!qcData, 
          summaryExists: !!qcData?.summary,
          summaryKeys: qcData?.summary ? Object.keys(qcData.summary) : []
        });
      }
      
      // 🔍 COMPREHENSIVE DEBUG SUMMARY
      console.log(`📊 BATCH ANALYSIS COMPLETION SUMMARY for ${selectedBatch.batchNumber}:`);
      console.log(`   📦 Total samples processed: ${selectedBatch.samples.length}`);
      console.log(`   📊 batchAnalysisData state:`, batchAnalysisData);
      console.log(`   🔬 Total analysis readings created across all samples: ${selectedBatch.samples.reduce((total, sample) => {
        const sampleAnalysisData = batchAnalysisData[sample.id] || {};
        // 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering
        const elementsToAnalyze = getSAAElementsFromMethod(sample);
        return total + elementsToAnalyze.filter((element: string) => {
          const elementData = sampleAnalysisData[element];
          return elementData && typeof elementData.ppm === 'number';
        }).length;
      }, 0)}`);
      
      if (Object.keys(batchAnalysisData).length === 0) {
        console.log(`❌ PROBLEM: batchAnalysisData is empty! This means no ppm values were entered in the batch analysis dialog.`);
        console.log(`✅ SOLUTION: The analyst must enter ppm values in the table before clicking "Finaliser Analyse du Lot".`);
      } else {
        console.log(`✅ batchAnalysisData contains data for ${Object.keys(batchAnalysisData).length} samples`);
      }
      
      setBatchAnalysisDialog(false);
      setSelectedBatch(null);
      setBatchAnalysisData({});
      setBatchCommonValues({ referenceStandard: 'Certified Reference Material', detectionMethod: 'Atomic Absorption Spectroscopy' });
      // 🔬 Clear QC data
      setQcData(null);
      setQcAlerts([]);
      loadSAAAnalysisData(); // Refresh the dashboard
      
      setSnackbarMessage(`Analyse du lot ${selectedBatch.batchNumber} complétée - ${result.updated} échantillons envoyés pour validation`);
      setSnackbarOpen(true);
      
    } catch (error: any) {
      console.error('❌ Error completing batch analysis:', error);
      setError(`Erreur lors de la finalisation du lot: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get wavelength for element
  const getElementWavelength = (element: string): number => {
    const wavelengths: Record<string, number> = {
      'Fe': 248.3,
      'Cu': 324.7,
      'Pb': 283.3,
      'Zn': 213.9,
      'Ag': 328.1,
      'Au': 242.8,
      'Cd': 228.8,
      'Co': 240.7,
      'Mn': 279.5,
      'Ni': 232.0
    };
    return wavelengths[element] || 324.7;
  };

  const loadSAAAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🔥 CRITICAL FIX: Load ALL samples for SAA analysis (no limit for production)
      // Include multiple statuses for multi-lab workflow support
      // 🔧 FIX: Added Physico statuses for multi-lab samples
      const statuses = [
        SampleStatus.LABORATOIRE_SAA_ANALYSE,
        SampleStatus.LABORATOIRE_ICP_ANALYSE,
        SampleStatus.LABORATOIRE_AU_ANALYSE,
        SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE, // 🔧 FIX: Added Physico base status
        SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE, // 🔧 FIX: Added Physico analysis status
        SampleStatus.VALIDATION_SERVICE, // 🔧 FIX: Include for multi-lab samples awaiting SAA analysis
        SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE, // 🔧 FIX: Multi-lab SAA+ICP samples in ICP prep
      ];
      
      const countResponse = await samplesService.getSamples(1, 1, {
        status: statuses,
      });
      const totalSamples = countResponse.total || 1000;
      
      // 🔧 FIX: Increased limit from 1000 to 5000 to capture all SAA-relevant samples
      // Then fetch all samples
      const response = await samplesService.getSamples(1, Math.min(totalSamples, 5000), {
        status: statuses,
      });
      
      // Filter samples that should appear in SAA Analyst Dashboard
      const saaAnalysisSamples = response.samples.filter(sample => {
        // Regular SAA samples (non-multi-lab)
        if (sample.statut === SampleStatus.LABORATOIRE_SAA_ANALYSE &&
            !sample.metadonnees?.multi_lab_workflow) {
          return true;
        }
        
        // Multi-lab samples with SAA elements that completed SAA prep but not SAA analysis
        const isMultiLab = sample.metadonnees?.multi_lab_workflow === true;
        if (isMultiLab) {
          const selectedElements = sample.metadonnees?.selected_elements || [];
          const elementRouting = sample.metadonnees?.element_lab_routing || {};
          // 🔧 FIX: Check if ANY selected element routes to SAA (not just elementRouting values)
          const hasSaaElements = selectedElements.some((element: string) => 
            elementRouting[element] === 'LABORATOIRE_SAA'
          );
          // 🔧 FIX: Also check laboratories_involved as fallback
          const hasSaaInLabs = sample.laboratories_involved?.includes('LABORATOIRE_SAA') || false;
          const saaPrepDone = sample.metadonnees?.saa_preparation_completed === true;
          const saaAnalysisNotDone = sample.metadonnees?.saa_analysis_completed !== true;
          return (hasSaaElements || hasSaaInLabs) && saaPrepDone && saaAnalysisNotDone;
        }
        
        return false;
      });
      
      setSamples(saaAnalysisSamples);
      
      // Calculate stats
      const urgentSamples = saaAnalysisSamples.filter(
        sample => sample.priorite === Priority.URGENTE
      ).length;
      
      const inProgressSamples = saaAnalysisSamples.filter(
        sample => sample.metadonnees?.analysis_status === 'IN_PROGRESS'
      ).length;
      
      const completedToday = saaAnalysisSamples.filter(sample => {
        const completedAt = sample.metadonnees?.saa_analysis_completed_at;
        if (!completedAt || typeof completedAt !== 'string') return false;
        const today = new Date().toDateString();
        return new Date(completedAt).toDateString() === today;
      }).length;
      
      setStats(prev => ({
        ...prev,
        ready_for_analysis: saaAnalysisSamples.length,
        in_progress: inProgressSamples,
        completed_today: completedToday,
        urgent_samples: urgentSamples,
      }));
      
      // SAA samples loaded successfully
    } catch (error) {
      console.error('Error loading SAA analysis data:', error);
      setError('Erreur lors du chargement des données d\'analyse SAA');
    } finally {
      setLoading(false);
    }
  };

  const loadInstrumentStatus = async () => {
    try {
      // TEMPORARY: Skip API call and use fallback data until quality endpoints are implemented
      // TODO: Implement /api/v1/quality/instruments endpoint in backend
      
      // Use default values - SAA instrument is ready
      setInstrumentStatus({
        last_calibration: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        r_squared: 0.9987,
        lamp_hours: 1250,
        flame_stable: true,
        background_correction: true,
        status: 'READY',
        last_qc_check: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      });
    } catch (error) {
      console.error('Error loading instrument status:', error);
      // Set basic status on error
      setInstrumentStatus({
        last_calibration: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        r_squared: 0.9987,
        lamp_hours: 1250,
        flame_stable: true,
        background_correction: true,
        status: 'READY',
        last_qc_check: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      });
    }
  };

  const loadControlCharts = async () => {
    try {
      // TEMPORARY: Skip API call and use empty array until quality endpoints are implemented
      // TODO: Implement /api/v1/quality/control-charts endpoint in backend
      
      // Use empty array - control charts will show "No data available" message
      setControlCharts([]);
    } catch (error) {
      console.error('Error loading control charts:', error);
      setControlCharts([]);
    }
  };

  const loadPendingSamples = async () => {
    // Implementation of loadPendingSamples
  };

  const loadPreparationData = async () => {
    // Implementation of loadPreparationData
  };

  const loadMethodValidations = async () => {
    try {
      // TEMPORARY: Skip API call and use fallback data until quality endpoints are implemented
      // TODO: Implement /api/v1/quality/method-validations endpoint in backend
      
      // Use fallback data directly - Real SAA method validation data
      const validations: MethodValidation[] = [
        {
          element: 'Fe',
          lod_ppm: 0.100,
          loq_ppm: 0.300,
          linear_range_min_ppm: 0.1,
          linear_range_max_ppm: 50,
          accuracy_percent: 98.50,
          precision_rsd_percent: 1.8,
          recovery_min_percent: 95,
          recovery_max_percent: 105,
          expanded_uncertainty_percent: 4.0,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Pb',
          lod_ppm: 0.050,
          loq_ppm: 0.150,
          linear_range_min_ppm: 0.2,
          linear_range_max_ppm: 25,
          accuracy_percent: 99.20,
          precision_rsd_percent: 1.5,
          recovery_min_percent: 98,
          recovery_max_percent: 102,
          expanded_uncertainty_percent: 3.0,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Cu',
          lod_ppm: 0.025,
          loq_ppm: 0.075,
          linear_range_min_ppm: 0.1,
          linear_range_max_ppm: 20,
          accuracy_percent: 98.90,
          precision_rsd_percent: 2.0,
          recovery_min_percent: 96,
          recovery_max_percent: 104,
          expanded_uncertainty_percent: 3.5,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Zn',
          lod_ppm: 0.030,
          loq_ppm: 0.090,
          linear_range_min_ppm: 0.1,
          linear_range_max_ppm: 15,
          accuracy_percent: 97.50,
          precision_rsd_percent: 2.2,
          recovery_min_percent: 95,
          recovery_max_percent: 105,
          expanded_uncertainty_percent: 4.2,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Ag',
          lod_ppm: 0.010,
          loq_ppm: 0.030,
          linear_range_min_ppm: 0.05,
          linear_range_max_ppm: 5,
          accuracy_percent: 96.80,
          precision_rsd_percent: 3.0,
          recovery_min_percent: 94,
          recovery_max_percent: 106,
          expanded_uncertainty_percent: 5.0,
          validation_date: new Date().toISOString()
        }
      ];

      setMethodValidations(validations);
    } catch (error) {
      console.error('Error loading method validations:', error);
      // Use basic validations on error
      setMethodValidations([]);
    }
  };

  const handleStartAnalysis = async (sample: Sample) => {
    setSelectedSample(sample);
    
    // Load preparation data from sample metadata
    const preparationData = extractPreparationData(sample);
    
    // Initialize analysis form
    const newAnalysisForm: SAAAnalysisForm = {
      sample_id: sample.sample_id,
      preparation_data: preparationData,
      calibration_standards: [],
      quality_controls: [],
      analysis_readings: [],
      instrument_status: instrumentStatus,
      analysis_started_at: new Date().toISOString(),
      analysis_completed_at: null,
      analyst_id: user?.id || '',
      notes: '',
        status: 'IN_PROGRESS'
    };
    
    setAnalysisForm(newAnalysisForm);
    setCurrentTab(0);
    setAnalysisDialog(true);
  };

  const extractPreparationData = (sample: Sample): PreparationData | null => {
    const metadata = sample.metadonnees;
    if (!metadata) return null;
    
    // First check if we have saa_preparation data (from preparation technician)
    const saaPrep = metadata.saa_preparation;
    if (saaPrep) {
      return {
        initial_weight: saaPrep.initialWeight || 0.5,
        digestion_method: saaPrep.digestionMethod || metadata.digestion_method || 'HCl',
        initial_volume: saaPrep.initialVolume || 10,
        dilutions: saaPrep.dilutions || [],
        total_dilution_factor: saaPrep.totalDilutionFactor || 1,
        preparation_completed_at: saaPrep.timestamp || metadata.preparation_completed_at || new Date().toISOString(),
        technician_id: saaPrep.technician_id || 'unknown',
        technician_name: saaPrep.technician_name || 'Technicien SAA',
        vf1: saaPrep.vf1,
        vp1: saaPrep.vp1,
      };
    }
    
    // Fallback to direct metadata properties (legacy format)
    return {
      initial_weight: metadata.initial_weight || 2.5,
      digestion_method: metadata.digestion_method || 'Aqua Regia',
      initial_volume: metadata.initial_volume || 100,
      dilutions: metadata.dilutions || [],
      total_dilution_factor: metadata.total_dilution_factor || 100,
      preparation_completed_at: metadata.preparation_completed_at || new Date().toISOString(),
      technician_id: metadata.technician_id || 'tech-001',
      technician_name: metadata.technician_name || 'Technicien SAA',
    };
  };

  const calculateConcentration = (
    absorbance: number,
    calibrationSlope: number,
    calibrationIntercept: number,
    dilutionFactor: number,
    sampleWeight: number,
    finalVolume: number,
    element: string,
    choixDeDilution: 0 | 1 | 2 = 1,
    // NEW: Additional parameters for choice 2
    vf1?: number,
    vp1?: number
  ): { ppm: number; percent: number; belowLOD: boolean; belowLOQ: boolean } => {
    // Concentration in ppm from calibration curve (L = lecture instrument)
    const ppm = (absorbance - calibrationIntercept) / calibrationSlope;
    
    // Final concentration percentage based on dilution choice
    let percent: number;
    if (choixDeDilution === 0) {
      // Simplified formula: Concentration = L × (V/p)
      percent = (ppm * finalVolume) / (sampleWeight * 10000);
    } else if (choixDeDilution === 1) {
      // Complete formula: Concentration = L × (V/p) × (vf/vp)
      percent = (ppm * dilutionFactor * finalVolume) / (sampleWeight * 10000);
    } else if (choixDeDilution === 2 && vf1 && vp1) {
      // Alternative complete formula: Concentration = L × (V/p) × (vf1/vp1)
      const alternativeDilutionFactor = vf1 / vp1;
      percent = (ppm * alternativeDilutionFactor * finalVolume) / (sampleWeight * 10000);
    } else {
      // Default to complete formula if choice 2 is selected but vf1/vp1 are missing
      percent = (ppm * dilutionFactor * finalVolume) / (sampleWeight * 10000);
    }
    
    // Check against method validation limits
    const validation = methodValidations.find(v => v.element === element);
    const belowLOD = validation ? ppm < validation.lod_ppm : false;
    const belowLOQ = validation ? ppm < validation.loq_ppm : false;
    
    return { ppm, percent, belowLOD, belowLOQ };
  };

  const addCalibrationStandard = () => {
    if (!analysisForm) return;
    
    const newStandard: CalibrationStandard = {
      id: Date.now().toString(),
      element: 'Au',
      concentration_ppm: 0,
      absorbance: 0,
      used_at: new Date().toISOString(),
    };
    
    setAnalysisForm({
      ...analysisForm,
      calibration_standards: [...analysisForm.calibration_standards, newStandard],
    });
  };

  const addQualityControl = () => {
    if (!analysisForm) return;
    
    const newQC: QualityControl = {
      id: Date.now().toString(),
      type: 'BLANK',
      element: 'Au',
      measured_value: 0,
      absorbance: 0,
      within_limits: true,
      timestamp: new Date().toISOString(),
    };
    
    setAnalysisForm({
      ...analysisForm,
      quality_controls: [...analysisForm.quality_controls, newQC],
    });
  };

  const addAnalysisReading = () => {
    if (!analysisForm) return;
    
    const newReading: AnalysisReading = {
      id: Date.now().toString(),
      element: 'Cu', // Default to Cu, user can change
      absorbance: 0,
      concentration_ppm: 0,
      concentration_final_percent: 0,
      uncertainty: 0.05, // Default uncertainty
      timestamp: new Date().toISOString(),
      instrument_settings: {
        wavelength: 324.7, // Cu wavelength
        slit_width: 0.5,
        flame_type: 'Air-Acetylene',
        lamp_current: 4
      }
    };
    
    setEditingReading(newReading);
    setEditDialogOpen(true);
  };

  const handleEditReading = (reading: AnalysisReading) => {
    setEditingReading({ ...reading });
    setEditDialogOpen(true);
  };

  const handleSaveReading = () => {
    if (!analysisForm || !editingReading) return;

    // Calculate concentrations based on absorbance using REAL preparation data
    const calibrationSlope = 0.1; // This should come from calibration data
    const calibrationIntercept = 0.001;
    
    // Use REAL preparation data from the sample (not hardcoded values!)
    const dilutionFactor = analysisForm.preparation_data?.total_dilution_factor || 1; // Real dilution (e.g., 1, not 250)
    const sampleWeight = analysisForm.preparation_data?.initial_weight || 0.5; // Real weight (e.g., 0.5g, not 2.5g)
    const finalVolume = analysisForm.preparation_data?.initial_volume || 1; // Real volume (e.g., 1mL, not 250mL)

    const concentrations = calculateConcentration(
      editingReading.absorbance,
      calibrationSlope,
      calibrationIntercept,
      dilutionFactor,
      sampleWeight,
      finalVolume,
      editingReading.element,
      1 // Default to complete formula for individual samples
    );

    const updatedReading: AnalysisReading = {
      ...editingReading,
      concentration_ppm: concentrations.ppm,
      concentration_final_percent: concentrations.percent,
      timestamp: new Date().toISOString()
    };

    const existingIndex = analysisForm.analysis_readings.findIndex(r => r.id === editingReading.id);
    let updatedReadings;

    if (existingIndex >= 0) {
      // Update existing reading
      updatedReadings = [...analysisForm.analysis_readings];
      updatedReadings[existingIndex] = updatedReading;
    } else {
      // Add new reading
      updatedReadings = [...analysisForm.analysis_readings, updatedReading];
    }

    setAnalysisForm({
      ...analysisForm,
      analysis_readings: updatedReadings
    });

    setEditDialogOpen(false);
    setEditingReading(null);
    setSnackbarMessage('Lecture d\'analyse sauvegardée avec succès');
    setSnackbarOpen(true);
  };

  const handleDeleteReading = (readingId: string) => {
    if (!analysisForm) return;

    const updatedReadings = analysisForm.analysis_readings.filter(r => r.id !== readingId);
    setAnalysisForm({
      ...analysisForm,
      analysis_readings: updatedReadings
    });

    setSnackbarMessage('Lecture d\'analyse supprimée');
    setSnackbarOpen(true);
  };

  const handleCompleteAnalysis = async () => {
    if (!selectedSample || !analysisForm) return;
    
    try {
      // Complete the analysis form
      const completedForm = {
        ...analysisForm,
        analysis_completed_at: new Date().toISOString(),
        status: 'COMPLETED' as const
      };
      
      // Update sample with analysis results
      await samplesService.updateSample(selectedSample.id, {
        metadonnees: {
          ...selectedSample.metadonnees,
          saa_analysis_form: completedForm,
          saa_analysis_completed: true, // 🔥 CRITICAL FIX: Required for SAA Supervisor to detect completed samples
          saa_analysis_completed_at: new Date().toISOString(),
          saa_analyst_id: user?.id,
          saa_analyst_name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
          analysis_status: 'COMPLETED',
          // Store a summary of results for quick access
          saa_analysis_summary: {
            elements_analyzed: completedForm.analysis_readings.map(r => r.element),
            total_readings: completedForm.analysis_readings.length,
            dilution_factor: completedForm.preparation_data?.total_dilution_factor,
            completion_date: new Date().toISOString()
          }
        }
      });
      
      // Move sample to validation status
      await samplesService.updateSampleStatus(
        selectedSample.id,
        SampleStatus.VALIDATION_SERVICE,
        'Analyse SAA terminée - Prêt pour validation',
        'LABORATOIRE_SAA'
      );
      
      setAnalysisDialog(false);
      setSelectedSample(null);
      setAnalysisForm(null);
      loadSAAAnalysisData();
      
      setSnackbarMessage('Analyse complétée et envoyée pour validation');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error completing analysis:', error);
      setError('Erreur lors de la finalisation de l\'analyse');
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return 'error';
      case Priority.ELEVEE:
        return 'warning';
      case Priority.NORMALE:
        return 'default';
      default:
        return 'default';
    }
  };

  const getInstrumentStatusColor = (status: string) => {
    switch (status) {
      case 'READY':
        return 'success';
      case 'CALIBRATING':
        return 'warning';
      case 'MAINTENANCE':
        return 'info';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleImportResults = (results: ImportedResultRow[]) => {
    setImportedResults(results);
    
    // Auto-populate form fields from imported data
    if (results.length > 0) {
      const firstResult = results[0];
      
      // Update preparation data if available
      if (firstResult.lab_weight && analysisForm?.preparation_data) {
        setAnalysisForm(prev => prev ? ({
          ...prev,
          preparation_data: prev.preparation_data ? {
            ...prev.preparation_data,
            initial_weight: firstResult.lab_weight || prev.preparation_data.initial_weight
          } : null
        }) : null);
      }
      
      if (firstResult.dilution_factor && analysisForm?.preparation_data) {
        setAnalysisForm(prev => prev ? ({
          ...prev,
          preparation_data: prev.preparation_data ? {
            ...prev.preparation_data,
            total_dilution_factor: firstResult.dilution_factor || prev.preparation_data.total_dilution_factor
          } : null
        }) : null);
      }

      // Create new analysis readings from imported data
      const newReadings: AnalysisReading[] = [];
      availableSAAElements.forEach((element) => {
        if (firstResult[element] !== undefined) {
          const concentration_ppm = firstResult[element] * 1; // Simple conversion
          // CRITICAL FIX: Use correct ppm to percentage conversion (divide by 10,000)
          const concentration_final_percent = concentration_ppm / 10000;
          
          newReadings.push({
            id: `imported-${element}-${Date.now()}`,
            element: element,
            absorbance: firstResult[element],
            concentration_ppm: concentration_ppm,
            concentration_final_percent: concentration_final_percent,
            uncertainty: 0.05, // Default uncertainty
            timestamp: new Date().toISOString(),
            instrument_settings: {
              wavelength: 0, // Will be set based on element
              slit_width: 0.2,
              flame_type: 'Air-Acetylene',
              lamp_current: 5
            }
          });
        }
      });
      
      if (newReadings.length > 0) {
        setAnalysisForm(prev => prev ? ({
          ...prev,
          analysis_readings: [...prev.analysis_readings, ...newReadings]
        }) : null);
      }
    }

    setSnackbarMessage(`${results.length} résultats importés avec succès`);
    setSnackbarOpen(true);
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  // ✅ CRITICAL FIX: Method-based element determination for SAA
  // 🔥 ENHANCED: For multi-lab samples, only show elements routed to SAA lab
  // 🔧 FIX: Must filter by BOTH selected_elements AND element_lab_routing
  const getSAAElementsFromMethod = (sample: Sample): string[] => {
    // 🔧 CRITICAL FIX: Start with selected_elements, then filter by routing
    // This ensures elements removed per-sample are properly greyed out
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const isMultiLab = sample.metadonnees?.multi_lab_workflow === true ||
                       Object.keys(elementRouting).length > 0;
    
    if (isMultiLab && selectedElements.length > 0) {
      // Filter selected_elements to only those routed to SAA lab
      const saaElements = selectedElements.filter((element: string) => 
        elementRouting[element] === 'LABORATOIRE_SAA'
      );
      
      if (saaElements.length > 0) {
        console.log(`🔍 SAA Elements for ${sample.sample_id} (filtered by selected_elements):`, saaElements);
        return saaElements;
      }
    }
    
    // 2. Direct selected_elements array (for non-multi-lab samples)
    if (selectedElements.length > 0) {
      // For regular SAA samples (no multi-lab), show all selected elements
      // that are valid SAA elements
      const validSaaElements = ['Ag', 'Au', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
      const saaElements = selectedElements.filter((el: string) => 
        validSaaElements.includes(el)
      );
      if (saaElements.length > 0) {
        return saaElements;
      }
      return selectedElements;
    }
    
    // 3. From analyses_demandees (analysis methods requested)
    if (sample.metadonnees?.analyses_demandees && Array.isArray(sample.metadonnees.analyses_demandees)) {
      // Map SAA methods to their complete element lists (same as backend)
      const methodToElements: {[key: string]: string[]} = {
        'AA-PRE.MO/ANA/002': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'], // SAA Standard
        'SAA': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'], // SAA Generic
        'SAA-MULTI': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'], // SAA Multi-element
      };
      
      const elements = new Set<string>();
      sample.metadonnees.analyses_demandees.forEach(method => {
        const methodElements = methodToElements[method] || ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
        methodElements.forEach(el => elements.add(el));
      });
      
      if (elements.size > 0) {
        return Array.from(elements);
      }
    }
    
    // 4. Fallback to standard SAA elements (from backend method mapping)
    return ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
  };

  // SAA Directory Scanning and Import Functions
  const getBatchSAAElements = (samples: Sample[]): string[] => {
    const allElements = new Set<string>();
    samples.forEach(s => {
      getSAAElementsFromMethod(s).forEach(el => allElements.add(el));
    });
    return Array.from(allElements);
  };

  const scanSAADirectory = async () => {
    try {
      setImportLoading(true);
      const { files } = await apiService.get<{ files: any[] }>('/saa/scan-directory');
      
      // For individual sample analysis, use current selected sample
      const sampleData = selectedSample ? [{ 
        id: selectedSample.id, 
        sample_id: selectedSample.sample_id 
      }] : [];
      
      const filesWithMatches = await Promise.all(
        files.map(async (file: any) => {
          const matches = await checkSAAFileMatches(file.path, sampleData);
          return {
            filename: file.name,
            path: file.path,
            size: file.size,
            modified: file.modified,
            matchedSamples: matches
          };
        })
      );
      
      setAvailableFiles(filesWithMatches);
      setFileImportDialogOpen(true);
    } catch (error) {
      console.error('Error scanning SAA directory:', error);
      setError('Erreur lors du scan du répertoire SAA');
    } finally {
      setImportLoading(false);
    }
  };

  // SAA Directory Scanning for Batch Analysis
  const scanSAADirectoryForBatch = async () => {
    try {
      setImportLoading(true);
      const { files } = await apiService.get<{ files: any[] }>('/saa/scan-directory');
      
      // For batch analysis, use all batch samples
      const batchSampleData = selectedBatch?.samples.map(sample => ({ 
        id: sample.id, 
        sample_id: sample.sample_id 
      })) || [];
      
      const filesWithMatches = await Promise.all(
        files.map(async (file: any) => {
          const matches = await checkSAAFileMatches(file.path, batchSampleData);
          return {
            filename: file.name,
            path: file.path,
            size: file.size,
            modified: file.modified,
            matchedSamples: matches
          };
        })
      );
      
      setAvailableFiles(filesWithMatches);
      setFileImportDialogOpen(true);
    } catch (error) {
      console.error('Error scanning SAA directory for batch:', error);
      setError('Erreur lors du scan du répertoire SAA pour le lot');
    } finally {
      setImportLoading(false);
    }
  };
  
  const checkSAAFileMatches = async (filePath: string, sampleData: Array<{id: string, sample_id: string}>): Promise<string[]> => {
    try {
      const { matches } = await apiService.post<{ matches: string[] }>('/saa/check-file-matches', {
        filePath,
        batchSamples: sampleData
      });
      return matches;
    } catch (error) {
      console.error('Error checking SAA file matches:', error);
      return [];
    }
  };
  
  const handleImportSAAFile = async (filename: string) => {
    try {
      setImportLoading(true);
      
      // Check if we're in batch mode or individual mode
      if (selectedBatch) {
        // Batch import mode
        const batchSampleData = selectedBatch.samples.map(sample => ({ 
          id: sample.id, 
          sample_id: sample.sample_id 
        }));
        
        // Parse SAA file and get results (EXACTLY like ICP does)
        // 🔥 NEW: Pass element-level dilution params to backend
        console.log('🔥 Sending elementDilutionParams to backend:', elementDilutionParams);
        
        const response = await apiService.post<{ 
          results: any[]; 
          totalSamples: number; 
          totalElements: number;
          importSummary?: {
            methodElements: string[];
            importedElements: string[];
            missingElements: string[];
            filteredOutElements: string[];
          };
          qcData?: QCData; // 🔬 NEW: QC data from CSV
        }>('/saa/parse-file', {
          filename,
          batchSamples: batchSampleData,
          elementDilutionParams: elementDilutionParams  // 🔥 NEW: Element-level dilution params
        });

        console.log('🔍 SAA API Response:', response);
        console.log('🔍 SAA Results length:', response.results?.length);
        console.log('🔬 SAA QC Data:', response.qcData);

        const { results, qcData: importedQcData } = response;
        
        // 🔬 Get expected elements FIRST (elements selected for SAA analysis in this batch)
        const expectedElements = selectedBatch?.samples?.length ? getBatchSAAElements(selectedBatch.samples) : ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
        console.log('🔬 Expected SAA elements for this batch:', expectedElements);
        
        // 🔬 Process QC data and generate alerts - FILTER BY SELECTED ELEMENTS ONLY
        if (importedQcData) {
          // 🔥 FILTER: Only keep QC data for elements that are selected for SAA analysis
          const filteredSummary: Record<string, any> = {};
          Object.entries(importedQcData.summary || {}).forEach(([element, summary]) => {
            if (expectedElements.includes(element)) {
              filteredSummary[element] = summary;
            }
          });
          
          const filteredReadings = (importedQcData.qcReadings || []).filter(
            (r: any) => expectedElements.includes(r.element)
          );
          
          console.log('🔬 Original QC elements:', Object.keys(importedQcData.summary || {}));
          console.log('🔬 Filtered QC elements (matching selected):', Object.keys(filteredSummary));
          
          // Set FILTERED QC data
          setQcData({
            ...importedQcData,
            summary: filteredSummary,
            qcReadings: filteredReadings
          });
          
          // Generate alerts from FILTERED QC data only
          const alerts: QCAlert[] = [];
          const TQ_RSD_THRESHOLD = 5; // % RSD threshold for TQ consistency
          const BLANK_THRESHOLD = 0.1; // Threshold for blanks
          
          Object.entries(filteredSummary).forEach(([element, summary]) => {
            const elementSummary = summary as QCElementSummary;
            
            // TQ Consistency Alert (RSD > 5%)
            if (elementSummary.tq_count > 1 && elementSummary.tq_rsd > TQ_RSD_THRESHOLD) {
              alerts.push({
                element,
                type: 'TQ_CONSISTENCY',
                severity: 'WARNING',
                message: `${element}: RSD des TQ (${elementSummary.tq_rsd.toFixed(1)}%) dépasse ${TQ_RSD_THRESHOLD}%`,
                value: elementSummary.tq_rsd,
                threshold: TQ_RSD_THRESHOLD,
              });
            } else if (elementSummary.tq_count > 1) {
              alerts.push({
                element,
                type: 'TQ_CONSISTENCY',
                severity: 'OK',
                message: `${element}: TQ OK (RSD: ${elementSummary.tq_rsd.toFixed(1)}%)`,
                value: elementSummary.tq_rsd,
              });
            }
            
            // Single TQ Alert
            if (elementSummary.tq_count === 1) {
              alerts.push({
                element,
                type: 'SINGLE_TQ',
                severity: 'INFO',
                message: `${element}: 1 seul TQ - RSD non calculable`,
              });
            }
            
            // No TQ Alert
            if (elementSummary.tq_count === 0) {
              alerts.push({
                element,
                type: 'NO_TQ',
                severity: 'CRITICAL',
                message: `${element}: Aucun TQ trouvé dans le fichier`,
              });
            }
            
            // Blank Alert
            if (elementSummary.blank_count > 0 && Math.abs(elementSummary.blank_average) > BLANK_THRESHOLD) {
              alerts.push({
                element,
                type: 'BLANK_HIGH',
                severity: 'WARNING',
                message: `${element}: Blanc moyen (${elementSummary.blank_average.toFixed(4)}) élevé`,
                value: elementSummary.blank_average,
                threshold: BLANK_THRESHOLD,
              });
            }
          });
          
          setQcAlerts(alerts);
          console.log('🔬 Generated QC Alerts (filtered):', alerts);
        }
        
        // Initialize batch data with default values (EXACTLY like ICP)
        const newBatchData: BatchAnalysisData = {};
        
        // Initialize all samples with all expected elements (default values)
        selectedBatch?.samples.forEach(sample => {
          newBatchData[sample.id] = {};
          
          expectedElements.forEach(element => {
            newBatchData[sample.id][element] = {
              ppm: 0, // Default value
              timestamp: new Date().toISOString(),
              originalReading: undefined,
              calculatedFromFormula: false
            };
          });
        });
        
        // Override with actual imported values (EXACTLY like ICP)
        let totalFormulaCalculations = 0;
        results.forEach((result: any) => {
          const sample = selectedBatch.samples.find(s => s.sample_id === result.sample_reference);
          if (sample) {
            console.log(`🔍 SAA RESULT: ${result.sample_reference} - Element: "${result.element}" - Concentration: ${result.concentration_ppm} ppm`);
            
            if (expectedElements.includes(result.element)) {
              newBatchData[sample.id][result.element] = {
                ppm: result.concentration_ppm, // Calculated with L × V/p × vf/vp
                timestamp: result.timestamp,
                originalReading: result.reading_value, // Original L value
                calculatedFromFormula: true
              };
              totalFormulaCalculations++;
              
              console.log(`✅ ${result.sample_reference} - ${result.element}: ${result.concentration_ppm} ppm (calculated from L=${result.reading_value})`);
            } else {
              console.warn(`⚠️ Skipping ${result.element} for ${result.sample_reference} - not in expected columns`);
            }
          }
        });
        
        // Update form state so user can see the values (EXACTLY like ICP)
        setBatchAnalysisData(newBatchData);
        setFileImportDialogOpen(false);
        
        const totalImported = Object.values(newBatchData).reduce((sum, sampleData) => 
          sum + Object.values(sampleData).filter(el => typeof el.ppm === 'number').length, 0
        );
        
        setSnackbarMessage(`✅ ${totalImported} calculs SAA (L × V/p × vf/vp) importés depuis ${filename}. Formule appliquée automatiquement!`);
        setSnackbarOpen(true);
        
        console.log('🧮 SAA Import Summary:', {
          filename,
          totalSamples: selectedBatch?.samples.length || 0,
          totalImported,
          totalExpected: expectedElements.length * (selectedBatch?.samples.length || 0),
          allFieldsFilled: Object.values(newBatchData).every(sampleData => 
            Object.keys(sampleData).length === expectedElements.length
          )
        });
      } else if (selectedSample) {
        // Individual import mode
        const sampleData = [{ 
          id: selectedSample.id, 
          sample_id: selectedSample.sample_id 
        }];
        
        // Parse SAA file and get results
        const response = await apiService.post<{ 
          results: any[]; 
          totalSamples: number; 
          totalElements: number;
        }>('/saa/parse-file', {
          filename,
          batchSamples: sampleData
        });

        const { results } = response;
        
        // Process and populate analysis form with SAA results
        if (results.length > 0) {
          // Create analysis readings from SAA results
          const newReadings: AnalysisReading[] = results.map(result => ({
            id: `saa-import-${result.element}-${Date.now()}`,
            element: result.element,
            absorbance: result.reading_value, // Store original reading (L value)
            concentration_ppm: result.concentration_ppm,
            concentration_final_percent: result.concentration_percent,
            uncertainty: 0.05,
            timestamp: new Date().toISOString(),
            instrument_settings: {
              wavelength: getElementWavelength(result.element),
              slit_width: 0.2,
              flame_type: 'Air-Acetylene',
              lamp_current: 5
            }
          }));
          
          // Update analysis form with imported readings
          if (analysisForm) {
            setAnalysisForm({
              ...analysisForm,
              analysis_readings: [...analysisForm.analysis_readings, ...newReadings]
            });
          }
          
          setSnackbarMessage(`✅ ${results.length} résultats SAA importés depuis ${filename}`);
          setSnackbarOpen(true);
        }
      }
      
      setFileImportDialogOpen(false);
      
    } catch (error) {
      console.error('Error importing SAA file:', error);
      setError('Erreur lors de l\'import du fichier SAA');
    } finally {
      setImportLoading(false);
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: 3,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Chargement du tableau de bord SAA...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    }}>
      {/* 🎨 MODERN HEADER - MATCHING ICP DESIGN */}
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Science color="primary" sx={{ fontSize: 40 }} />
        Tableau de Bord - Analyste SAA
        <Chip 
          label={`${samples.length} échantillons`}
          color="primary" 
          variant="outlined"
        />
                <Chip 
                  label={instrumentStatus.status} 
                  color={getInstrumentStatusColor(instrumentStatus.status)}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 3 }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* 🎨 ENHANCED STATISTICS CARDS - MATCHING ICP DESIGN */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Prêts pour analyse
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.ready_for_analysis}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}>
                  📋
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.secondary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.secondary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    En cours d'analyse
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.in_progress}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.secondary.main, 0.3)}`,
                }}>
                  ⏳
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.success.main}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': { 
                transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
                borderRadius: '50%',
                transform: 'translate(50%, -50%)',
              }
            }}>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Terminés aujourd'hui
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                    }}>
                    {stats.completed_today}
                    </Typography>
                </Box>
                <Box sx={{ 
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                }}>
                  ✅
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.error.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Échantillons urgents
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.urgent_samples}
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                  }}>
                  🚨
                  </Box>
                </Box>
              </CardContent>
            </Card>
      </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.info.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.info.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.info.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Disponibilité instrument
          </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.instrument_uptime}%
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                }}>
                  ⚡
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    R² moyen calibration
              </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.average_r_squared.toFixed(4)}
              </Typography>
            </Box>
                <Box sx={{ 
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                }}>
                  📊
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 🎨 MODERN BATCH AND SAMPLES VISUALIZATION - MATCHING ICP DESIGN */}
                      {(() => {
        // Group samples by batch (preserve SAA logic)
                        const { batches, individualSamples } = groupSamplesByBatch(samples);
        
        // Convert batches object to array format for rendering (like ICP)
        const batchesArray = Object.entries(batches).map(([batchNumber, batchSamples]) => ({
          batchNumber,
          samples: batchSamples.sort((a, b) => (a.metadonnees?.batch_info?.sample_position || 0) - (b.metadonnees?.batch_info?.sample_position || 0)),
          totalSamples: batchSamples[0]?.metadonnees?.batch_info?.total_samples || batchSamples.length,
          isComplete: batchSamples.length === (batchSamples[0]?.metadonnees?.batch_info?.total_samples || batchSamples.length)
        }));
                            
                            return (
          <>
            {/* Batch Analysis Section */}
            {batchesArray.length > 0 && (
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 3,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                }
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}>
                      📋
                    </Box>
                    Analyse par Lots SAA ({batchesArray.length} lots)
                  </Typography>
                
                  {batchesArray.map((batchItem, index) => (
                    <Accordion 
                      key={batchItem.batchNumber} 
                      expanded={expandedBatches.has(batchItem.batchNumber)} 
                      onChange={() => toggleBatchExpansion(batchItem.batchNumber)}
                                  sx={{ 
                        mb: 1,
                        background: index % 2 === 0 
                          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.info.main, 0.02)})`
                          : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        borderRadius: '12px !important',
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                        '&:hover': {
                          transform: 'translateX(4px)',
                          transition: 'transform 0.2s ease',
                        }
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMore />}
                        sx={{
                          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.info.main, 0.05)})`,
                          borderRadius: '12px',
                          '&:hover': {
                            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.info.main, 0.1)})`,
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 'bold',
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}>
                                        {batchItem.batchNumber}
                                      </Typography>
                          <Box display="flex" gap={1} mr={2}>
                                      <Chip 
                              label={`${batchItem.samples.length}/${batchItem.totalSamples} échantillons`}
                              color={batchItem.isComplete ? 'success' : 'warning'}
                                        size="small" 
                              sx={{
                                background: batchItem.isComplete 
                                  ? `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                  : `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                                color: 'white',
                                fontWeight: 'bold',
                                boxShadow: `0 2px 8px ${alpha(batchItem.isComplete ? theme.palette.success.main : theme.palette.warning.main, 0.3)}`,
                              }}
                                      />
                                      <Chip 
                              label={batchItem.isComplete ? 'Complet' : 'En cours'}
                              color={batchItem.isComplete ? 'success' : 'info'}
                                        size="small" 
                              sx={{
                                background: batchItem.isComplete 
                                  ? `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                  : `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                                color: 'white',
                                fontWeight: 'bold',
                                boxShadow: `0 2px 8px ${alpha(batchItem.isComplete ? theme.palette.success.main : theme.palette.info.main, 0.3)}`,
                              }}
                            />
                                      <Button
                              size="small"
                                        variant="contained"
                              startIcon={<Analytics />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartBatchAnalysis(batchItem.batchNumber, batchItem.samples);
                              }}
                              sx={{ 
                                ml: 1,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                                }
                              }}
                            >
                              Analyser le Lot
                                      </Button>
                                      <Button
                              size="small"
                                        variant="outlined"
                                        color="warning"
                                        startIcon={sendingToPrepBatch ? <CircularProgress size={16} /> : <Settings />}
                              disabled={sendingToPrepBatch}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendBatchToPrepChimique(batchItem.batchNumber, batchItem.samples);
                              }}
                              sx={{ 
                                ml: 1,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                  borderColor: theme.palette.warning.main,
                                }
                              }}
                                      >
                                        {sendingToPrepBatch ? 'Envoi...' : 'Vers Prep'}
                                      </Button>
                                    </Box>
                                      </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Position</TableCell>
                              <TableCell>Sample ID</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell>Priorité</TableCell>
                              <TableCell>État</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {batchItem.samples.map((sample) => (
                              <TableRow key={sample.id}>
                                <TableCell>{sample.metadonnees?.batch_info?.sample_position || 0}</TableCell>
                                <TableCell>{sample.sample_id}</TableCell>
                                    <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={sample.priorite} 
                                        color={getPriorityColor(sample.priorite)}
                                    size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                  {sample.metadonnees?.saa_analysis_form?.analysis_readings?.length > 0 ? (
                                    <Chip label="Analysé" color="success" size="small" />
                                  ) : (
                                    <Chip label="En attente" color="default" size="small" />
                                  )}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                    variant="outlined"
                                        onClick={() => handleStartAnalysis(sample)}
                                    startIcon={<Analytics />}
                                      >
                                        Analyser
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Individual Samples Section - Modern Design */}
            <Card sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
              }
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold'
                }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}>
                    🔬
                  </Box>
                  Échantillons Individuels SAA ({individualSamples.length})
                </Typography>
                
                {individualSamples.length === 0 ? (
                  <Alert 
                    severity="info"
                    sx={{
                      background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: theme.palette.info.main
                      }
                    }}
                  >
                    Aucun échantillon individuel en attente d'analyse SAA.
                  </Alert>
                ) : (
                  <List>
                    {individualSamples.map((sample, index) => {
                      const analysisReadings = sample.metadonnees?.saa_analysis_form?.analysis_readings || [];
                      const preparationData = sample.metadonnees?.saa_preparation;
                      
                            return (
                        <ListItem
                          key={sample.id}
                          sx={{
                            border: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.2),
                            borderRadius: 2,
                            mb: 1,
                            background: sample.priorite === Priority.URGENTE 
                              ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})`
                              : index % 2 === 0
                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.info.main, 0.02)})`
                                : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateX(8px)',
                              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                              borderColor: alpha(theme.palette.primary.main, 0.4),
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Badge 
                              badgeContent={index + 1}
                              color="primary"
                              sx={{
                                '& .MuiBadge-badge': {
                                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                                }
                              }}
                            >
                              <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.2rem',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                              }}>
                                🧪
                              </Box>
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                                    {sample.sample_id}
                                  </Typography>
                                  <Chip 
                                    label={sample.priorite} 
                                    color={getPriorityColor(sample.priorite)}
                                  size="small"
                                />
                                {analysisReadings.length > 0 && (
                                  <Chip
                                    label={`${analysisReadings.length} éléments`}
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                  />
                                    )}
                                  </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                Client: {sample.client?.company_name || 'N/A'} | 
                                Réf: {sample.external_id || 'N/A'} |
                                Reçu: {sample.date_reception ? new Date(sample.date_reception).toLocaleDateString() : 'N/A'} |
                                Poids: {preparationData?.initialWeight || 'N/A'}g |
                                Méthode: {preparationData?.digestionMethod || 'N/A'}
                              </Typography>
                            }
                          />
                          <Box display="flex" gap={1}>
                                  <Button
                                    variant="contained"
                                    size="small"
                               startIcon={<CheckCircle />}
                                    onClick={() => handleStartAnalysis(sample)}
                               color={analysisReadings.length > 0 ? 'success' : 'secondary'}
                                  >
                               {analysisReadings.length > 0 ? 'Finaliser' : 'Commencer'}
                                  </Button>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
          )}
        </CardContent>
      </Card>
          </>
        );
      })()}

        {/* Instrument Status & Control Charts */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            {/* Instrument Status */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Settings color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      État Instrument SAA
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Dernière calibration</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(instrumentStatus.last_calibration).toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Coefficient R²</Typography>
                      <Typography variant="body2" color={instrumentStatus.r_squared > 0.995 ? 'success.main' : 'error.main'}>
                        {instrumentStatus.r_squared.toFixed(4)}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Heures lampe</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {instrumentStatus.lamp_hours}h
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Flamme stable</Typography>
                      <Chip 
                        label={instrumentStatus.flame_stable ? 'OUI' : 'NON'} 
                        color={instrumentStatus.flame_stable ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Refresh />}
                    size="small"
                  >
                    Recalibrer
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Control Charts Summary */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <BarChart color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Cartes de Contrôle
                    </Typography>
                  </Box>
                  
                  {controlCharts.slice(0, 3).map((chart) => {
                    const outOfControlCount = chart.measurements.filter(m => m.out_of_control).length;
                    return (
                      <Box key={chart.element} mb={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" fontWeight={500}>
                            {chart.element}
                          </Typography>
                          {outOfControlCount > 0 && (
                            <Chip 
                              label={`${outOfControlCount} hors limites`}
                              color="error"
                              size="small"
                            />
                          )}
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={((chart.measurements.length - outOfControlCount) / chart.measurements.length) * 100}
                          color={outOfControlCount > 0 ? 'error' : 'success'}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    );
                  })}
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Timeline />}
                    size="small"
                  >
                    Voir Graphiques
                  </Button>
                </CardContent>
              </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Comprehensive Analysis Dialog */}
      <Dialog 
        open={analysisDialog} 
        onClose={() => setAnalysisDialog(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Science color="primary" />
              <Typography variant="h5">
              Analyse SAA - {selectedSample?.sample_id}
            </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip 
                label={analysisForm?.status || 'DRAFT'} 
                color={analysisForm?.status === 'COMPLETED' ? 'success' : 'warning'}
              />
              <Typography variant="body2" color="text.secondary">
                {analysisForm?.analysis_started_at && 
                  `Démarré: ${new Date(analysisForm.analysis_started_at).toLocaleTimeString()}`
                }
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
              <Tab label="Données de Préparation" />
              <Tab label="Étalonnage" />
              <Tab label="Contrôles Qualité" />
              <Tab label="Lectures & Résultats" />
              <Tab label="Validation" />
              <Tab label="Limites Méthode" />
            </Tabs>
          </Box>

          {/* Tab 0: Preparation Data */}
          {currentTab === 0 && (
            <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Données de Préparation Récupérées Automatiquement
            </Typography>
            
              {analysisForm?.preparation_data ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Informations Générales
                        </Typography>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Poids initial:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {analysisForm.preparation_data.initial_weight} g
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Méthode digestion:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {analysisForm.preparation_data.digestion_method}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Volume initial:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {analysisForm.preparation_data.initial_volume} mL
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="primary" fontWeight={600}>
                            Facteur dilution total:
                          </Typography>
                          <Typography variant="body2" color="primary" fontWeight={600}>
                            {analysisForm.preparation_data.total_dilution_factor}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Technicien & Timing
                        </Typography>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Technicien:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {analysisForm.preparation_data.technician_name}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Préparation terminée:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date(analysisForm.preparation_data.preparation_completed_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {analysisForm.preparation_data.dilutions.length > 0 && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Historique des Dilutions
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Étape</TableCell>
                                  <TableCell align="right">Volume Prélevé (mL)</TableCell>
                                  <TableCell align="right">Volume Final (mL)</TableCell>
                                  <TableCell align="right">Facteur Dilution</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {analysisForm.preparation_data.dilutions.map((dilution, index) => (
                                  <TableRow key={index}>
                                    <TableCell>Dilution {index + 1}</TableCell>
                                    <TableCell align="right">{dilution.volumeTaken}</TableCell>
                                    <TableCell align="right">{dilution.finalVolume}</TableCell>
                                    <TableCell align="right">{dilution.dilutionFactor.toFixed(2)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Alert severity="warning">
                  Aucune donnée de préparation disponible. Contactez le technicien de préparation.
                </Alert>
              )}
            </Box>
          )}

          {/* Additional tabs would be added here - for now showing structure */}
          {currentTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Standards de Calibration</Typography>
                              <Alert severity="info">
                  Coefficient R² requis: &gt; 0.995 | Actuel: {instrumentStatus.r_squared.toFixed(4)}
                </Alert>
              {/* Calibration standards table would go here */}
            </Box>
          )}

          {currentTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Contrôles Qualité</Typography>
              <Alert severity="info">
                Blancs, standards de contrôle, duplicatas et ajouts dosés
              </Alert>
              {/* Quality controls table would go here */}
            </Box>
          )}

          {currentTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Lectures d'Absorbance et Calculs</Typography>
                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    startIcon={importLoading ? <CircularProgress size={16} /> : <CloudUpload />}
                    onClick={scanSAADirectory}
                    disabled={importLoading}
                  >
                    {importLoading ? 'Scan...' : 'Importer SAA'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addAnalysisReading}
                  >
                    Ajouter Lecture
                  </Button>
                </Box>
              </Box>
              

              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Formule Agilent 5100 ICP-OES:</strong> Concentration (ppm) = (Absorbance - Ordonnée) / Pente | 
                  Concentration finale (%) = (ppm × Dilution × Volume) / (Poids × 10,000)
                </Typography>
                {analysisForm?.preparation_data && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    <strong>Paramètres actuels:</strong> Pente=0.1, Ordonnée=0.001, 
                    Dilution={analysisForm.preparation_data.total_dilution_factor}, 
                    Poids={analysisForm.preparation_data.initial_weight}g, 
                    Volume={analysisForm.preparation_data.initial_volume}mL
                  </Typography>
                )}
              </Alert>

              {/* Detection Limits Alert */}
              {analysisForm?.analysis_readings && analysisForm.analysis_readings.length > 0 && (
                (() => {
                  const belowLODCount = analysisForm.analysis_readings.filter(reading => {
                    const validation = methodValidations.find(v => v.element === reading.element);
                    return validation ? reading.concentration_ppm < validation.lod_ppm : false;
                  }).length;
                  
                  const belowLOQCount = analysisForm.analysis_readings.filter(reading => {
                    const validation = methodValidations.find(v => v.element === reading.element);
                    return validation ? reading.concentration_ppm >= validation.lod_ppm && reading.concentration_ppm < validation.loq_ppm : false;
                  }).length;
                  
                  if (belowLODCount > 0 || belowLOQCount > 0) {
                    return (
                      <Alert severity={belowLODCount > 0 ? "error" : "warning"} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          ⚠️ Alertes Limites de Détection
                        </Typography>
                        {belowLODCount > 0 && (
                          <Typography variant="body2">
                            • {belowLODCount} résultat(s) en dessous de la LOD (Limite de Détection)
                          </Typography>
                        )}
                        {belowLOQCount > 0 && (
                          <Typography variant="body2">
                            • {belowLOQCount} résultat(s) entre LOD et LOQ (incertitude accrue)
                          </Typography>
                        )}
                      </Alert>
                    );
                  }
                  return null;
                })()
              )}

              {/* Analysis Readings Table */}
              {analysisForm?.analysis_readings && analysisForm.analysis_readings.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Élément</TableCell>
                        <TableCell align="right">Longueur d'onde (nm)</TableCell>
                        <TableCell align="right">Absorbance</TableCell>
                        <TableCell align="right">Concentration (ppm)</TableCell>
                        <TableCell align="right">Concentration finale (%)</TableCell>
                        <TableCell align="right">Incertitude</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisForm.analysis_readings.map((reading, index) => (
                        <TableRow key={reading.id || index}>
                          <TableCell>
                            <Chip label={reading.element} size="small" color="primary" />
                          </TableCell>
                          <TableCell align="right">
                            {reading.instrument_settings?.wavelength || 'N/A'}
                          </TableCell>
                          <TableCell align="right">
                            {reading.absorbance.toFixed(4)}
                          </TableCell>
                          <TableCell align="right">
                            {(() => {
                              const validation = methodValidations.find(v => v.element === reading.element);
                              const belowLOD = validation ? reading.concentration_ppm < validation.lod_ppm : false;
                              const belowLOQ = validation ? reading.concentration_ppm < validation.loq_ppm : false;
                              
                              if (belowLOD) {
                                return (
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" fontWeight={600} color="error">
                                      &lt; LOD
                                    </Typography>
                                    <Chip label="⚠️" size="small" color="error" />
                                  </Box>
                                );
                              } else if (belowLOQ) {
                                return (
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" fontWeight={600} color="warning">
                                      {reading.concentration_ppm.toFixed(3)}
                                    </Typography>
                                    <Chip label="!" size="small" color="warning" />
                                  </Box>
                                );
                              } else {
                                return (
                                  <Typography variant="body2" fontWeight={600}>
                                    {reading.concentration_ppm.toFixed(3)}
                                  </Typography>
                                );
                              }
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            {(() => {
                              const validation = methodValidations.find(v => v.element === reading.element);
                              const belowLOD = validation ? reading.concentration_ppm < validation.lod_ppm : false;
                              const belowLOQ = validation ? reading.concentration_ppm < validation.loq_ppm : false;
                              
                              if (belowLOD) {
                                return (
                                  <Typography variant="body2" fontWeight={600} color="error">
                                    &lt; LOD
                                  </Typography>
                                );
                              } else {
                                return (
                                  <Typography variant="body2" fontWeight={600} color={belowLOQ ? "warning" : "primary"}>
                                    {reading.concentration_final_percent.toFixed(4)}%
                                  </Typography>
                                );
                              }
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            ±{reading.uncertainty.toFixed(3)}
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={1} justifyContent="center">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleEditReading(reading)}
                                title="Modifier"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteReading(reading.id)}
                                title="Supprimer"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucune lecture d'analyse
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Importez vos résultats depuis un fichier CSV/Excel ou ajoutez les lectures manuellement
                  </Typography>
                  <Box display="flex" justifyContent="center" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<CloudUpload />}
                      onClick={() => setImportDialogOpen(true)}
                    >
                      Importer Fichier
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={addAnalysisReading}
                    >
                      Saisie Manuelle
                    </Button>
                  </Box>
                </Card>
              )}

              {/* Imported Results Summary */}
              {importedResults.length > 0 && (
                <Box mt={3}>
                  <Alert severity="success">
                    <Typography variant="subtitle2" gutterBottom>
                      Données importées avec succès: {importedResults.length} échantillons
                    </Typography>
                    <Typography variant="body2">
                      Les résultats ont été automatiquement intégrés dans le formulaire d'analyse.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          {currentTab === 4 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Validation Finale</Typography>
              <Alert severity="success">
                Analyse prête pour validation et transfert vers le service qualité
              </Alert>
              {/* Validation summary would go here */}
            </Box>
          )}

          {/* Tab 5: Method Validation Limits */}
          {currentTab === 5 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Limites de Détection et Validation de Méthode
              </Typography>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Élément</TableCell>
                      <TableCell align="right">LOD (ppm)</TableCell>
                      <TableCell align="right">LOQ (ppm)</TableCell>
                      <TableCell align="right">Plage Linéaire (ppm)</TableCell>
                      <TableCell align="right">Récupération (%)</TableCell>
                      <TableCell align="right">Incertitude (%)</TableCell>
                      <TableCell>Validation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {methodValidations.map((validation) => (
                      <TableRow key={validation.element}>
                        <TableCell>
                          <Chip label={validation.element} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="right">{validation.lod_ppm}</TableCell>
                        <TableCell align="right">{validation.loq_ppm}</TableCell>
                        <TableCell align="right">
                          {validation.linear_range_min_ppm} - {validation.linear_range_max_ppm}
                        </TableCell>
                        <TableCell align="right">
                          {validation.recovery_min_percent} - {validation.recovery_max_percent}
                        </TableCell>
                        <TableCell align="right">
                          ±{validation.expanded_uncertainty_percent}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={new Date(validation.validation_date).toLocaleDateString('fr-FR')}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Les résultats inférieurs à la LOD seront signalés comme "&lt; LOD".
                  Les résultats entre LOD et LOQ seront signalés avec une incertitude accrue.
                </Typography>
              </Alert>

              {/* Current Analysis Status vs Detection Limits */}
              {analysisForm?.analysis_readings && analysisForm.analysis_readings.length > 0 && (
                <Card sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>
                    📊 Status Analyse Actuelle vs Limites de Détection
                  </Typography>
                  <Grid container spacing={2}>
                    {analysisForm.analysis_readings.map((reading) => {
                      const validation = methodValidations.find(v => v.element === reading.element);
                      if (!validation) return null;
                      
                      const belowLOD = reading.concentration_ppm < validation.lod_ppm;
                      const belowLOQ = reading.concentration_ppm >= validation.lod_ppm && reading.concentration_ppm < validation.loq_ppm;
                      const status = belowLOD ? 'error' : belowLOQ ? 'warning' : 'success';
                      const statusText = belowLOD ? '&lt; LOD' : belowLOQ ? 'LOD-LOQ' : 'OK';
                      
                      return (
                        <Grid item xs={12} md={6} key={reading.element}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" p={1} 
                               sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                            <Box>
                              <Typography variant="subtitle2">{reading.element}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {reading.concentration_ppm.toFixed(3)} ppm
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip 
                                label={statusText} 
                                size="small" 
                                color={status}
                                variant={status === 'success' ? 'filled' : 'outlined'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                LOD: {validation.lod_ppm} | LOQ: {validation.loq_ppm}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={() => setAnalysisDialog(false)}>
            Annuler
          </Button>
          <Button 
            variant="outlined"
            startIcon={<Save />}
          >
            Sauvegarder Brouillon
          </Button>
          <Button 
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={handleCompleteAnalysis}
          >
            Finaliser Analyse
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingReading?.id && analysisForm?.analysis_readings.find(r => r.id === editingReading.id) 
            ? 'Modifier Lecture d\'Analyse' 
            : 'Nouvelle Lecture d\'Analyse'
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {/* Element Selection */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Élément</InputLabel>
                  <Select
                    value={editingReading?.element || ''}
                    onChange={(e) => {
                      if (!editingReading) return;
                      const element = e.target.value;
                      const wavelengths = {
                        'Ag': 328.1, 'Cd': 228.8, 'Co': 240.7, 'Cu': 324.7, 'Fe': 248.3,
                        'Mn': 279.5, 'Ni': 232.0, 'Pb': 217.0, 'Zn': 213.9
                      };
                      setEditingReading({
                        ...editingReading,
                        element,
                        instrument_settings: {
                          ...editingReading.instrument_settings!,
                          wavelength: wavelengths[element as keyof typeof wavelengths] || 0
                        }
                      });
                    }}
                    label="Élément"
                  >
                    {availableSAAElements.map(element => (
                      <MenuItem key={element} value={element}>{element}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Wavelength (auto-filled) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Longueur d'onde (nm)"
                  type="number"
                  value={editingReading?.instrument_settings?.wavelength || ''}
                  onChange={(e) => {
                    if (!editingReading) return;
                    setEditingReading({
                      ...editingReading,
                      instrument_settings: {
                        ...editingReading.instrument_settings!,
                        wavelength: parseFloat(e.target.value) || 0
                      }
                    });
                  }}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-rempli selon l'élément sélectionné"
                />
              </Grid>

              {/* Absorbance Input */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Absorbance"
                  type="number"
                  value={editingReading?.absorbance || ''}
                  onChange={(e) => {
                    if (!editingReading) return;
                    const value = parseFloat(e.target.value);
                    const updatedReading = {
                      ...editingReading,
                      absorbance: value || 0
                    };
                    
                    // Auto-calculate concentrations using REAL preparation data and dilution choice
                    if (value > 0 && analysisForm?.preparation_data) {
                      const calibrationSlope = 0.1; // Default calibration slope (should come from method validation)
                      const calibrationIntercept = 0.001; // Default intercept
                      const ppm = (value - calibrationIntercept) / calibrationSlope;
                      
                      // Use REAL preparation data from the sample
                      const realWeight = analysisForm.preparation_data.initial_weight; // Real weight (e.g., 0.5g)
                      const realDilution = analysisForm.preparation_data.total_dilution_factor; // Real dilution (e.g., 1)
                      const realVolume = analysisForm.preparation_data.initial_volume; // Real volume (e.g., 1mL)
                      
                      // Default to complete formula for individual samples
                      const percent = (ppm * realDilution * realVolume) / (realWeight * 10000);
                      
                      updatedReading.concentration_ppm = ppm;
                      updatedReading.concentration_final_percent = percent;
                    }
                    
                    setEditingReading(updatedReading);
                  }}
                  inputProps={{ 
                    step: 0.0001,
                    min: 0,
                    max: 2.0
                  }}
                  helperText="Valeur d'absorbance mesurée (0.0000 - 2.0000)"
                  required
                />
              </Grid>

              {/* Concentration (calculated) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Concentration (ppm)"
                  value={editingReading?.concentration_ppm?.toFixed(3) || '0.000'}
                  InputProps={{ readOnly: true }}
                  helperText="Calculée automatiquement"
                />
              </Grid>

              {/* Final Concentration */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Concentration Finale (%)"
                  value={editingReading?.concentration_final_percent?.toFixed(4) || '0.0000'}
                  InputProps={{ readOnly: true }}
                  helperText="Calculée avec facteur de dilution"
                />
              </Grid>

              {/* Uncertainty */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Incertitude"
                  type="number"
                  value={editingReading?.uncertainty || ''}
                  onChange={(e) => {
                    if (!editingReading) return;
                    setEditingReading({
                      ...editingReading,
                      uncertainty: parseFloat(e.target.value) || 0
                    });
                  }}
                  inputProps={{ 
                    step: 0.001,
                    min: 0
                  }}
                  helperText="Incertitude de mesure (±)"
                />
              </Grid>

              {/* Instrument Settings */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Paramètres Instrumentaux</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <TextField
                  fullWidth
                      label="Largeur de fente (nm)"
                      type="number"
                      value={editingReading?.instrument_settings?.slit_width || ''}
                      onChange={(e) => {
                        if (!editingReading) return;
                        setEditingReading({
                          ...editingReading,
                          instrument_settings: {
                            ...editingReading.instrument_settings!,
                            slit_width: parseFloat(e.target.value) || 0
                          }
                        });
                      }}
                      inputProps={{ step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Courant lampe (mA)"
                      type="number"
                      value={editingReading?.instrument_settings?.lamp_current || ''}
                      onChange={(e) => {
                        if (!editingReading) return;
                        setEditingReading({
                          ...editingReading,
                          instrument_settings: {
                            ...editingReading.instrument_settings!,
                            lamp_current: parseFloat(e.target.value) || 0
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Type de flamme"
                      value={editingReading?.instrument_settings?.flame_type || ''}
                      onChange={(e) => {
                        if (!editingReading) return;
                        setEditingReading({
                          ...editingReading,
                          instrument_settings: {
                            ...editingReading.instrument_settings!,
                            flame_type: e.target.value
                          }
                        });
                      }}
                    />
                  </Grid>
              </Grid>
            </Grid>

              {/* Calculated Results Preview */}
              {editingReading && editingReading.absorbance > 0 && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="subtitle2" gutterBottom>
                      Résultats Calculés (Aperçu)
                </Typography>
                    <Typography variant="body2">
                      • Concentration (ppm): {(((editingReading?.absorbance || 0) - 0.001) / 0.1).toFixed(3)}
                    </Typography>
                    <Typography variant="body2">
                      • Concentration finale (%): {(() => {
                        const ppm = ((editingReading?.absorbance || 0) - 0.001) / 0.1;
                        const realWeight = analysisForm?.preparation_data?.initial_weight || 0.5;
                        const realDilution = analysisForm?.preparation_data?.total_dilution_factor || 1;
                        const realVolume = analysisForm?.preparation_data?.initial_volume || 1;
                        
                        // Complete formula: L × (V/p) × (vf/vp)
                        return ((ppm * realDilution * realVolume) / (realWeight * 10000)).toFixed(4);
                      })()}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Formule: L × (V/p) × (vf/vp) | 
                      pente=0.1, ordonnée=0.001, 
                      dilution={analysisForm?.preparation_data?.total_dilution_factor || 1}, 
                      poids={analysisForm?.preparation_data?.initial_weight || 0.5}g, 
                      volume={analysisForm?.preparation_data?.initial_volume || 1}mL
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            variant="contained"
            onClick={handleSaveReading}
            disabled={!editingReading?.element || !editingReading?.absorbance}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      {/* SAA Directory File Selection Dialog */}
      <Dialog open={fileImportDialogOpen} onClose={() => setFileImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Science color="primary" />
            <Typography variant="h6">Fichiers SAA Disponibles</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Alert severity="info">
              <Typography variant="body2">
                Fichiers détectés dans le répertoire SAA. Sélectionnez le fichier contenant les données pour l'échantillon {selectedSample?.sample_id}.
              </Typography>
            </Alert>
          </Box>
          
          {availableFiles.length > 0 ? (
            <List>
              {availableFiles.map((file, index) => (
                <ListItem key={index} divider>
                  <ListItemIcon>
                    <FileUpload color={file.matchedSamples.length > 0 ? 'primary' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.filename}
                    secondary={
                      <span style={{ display: 'block' }}>
                        <Typography variant="body2" color="text.secondary" component="span" display="block">
                          Taille: {(file.size / 1024).toFixed(1)} KB | Modifié: {new Date(file.modified).toLocaleString()}
                        </Typography>
                        {file.matchedSamples.length > 0 ? (
                          <Typography variant="body2" color="success.main" component="span" display="block">
                            ✓ {file.matchedSamples.length} échantillon(s) trouvé(s): {file.matchedSamples.join(', ')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="warning.main" component="span" display="block">
                            ⚠ Aucun échantillon correspondant trouvé
                          </Typography>
                        )}
                      </span>
                    }
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleImportSAAFile(file.filename)}
                    disabled={file.matchedSamples.length === 0 || importLoading}
                    startIcon={importLoading ? <CircularProgress size={16} /> : <CloudUpload />}
                    color={file.matchedSamples.length > 0 ? 'primary' : 'inherit'}
                  >
                    {importLoading ? 'Import...' : 'Importer'}
                  </Button>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="warning">
              <Typography variant="body2">
                Aucun fichier SAA trouvé dans le répertoire. Vérifiez que les fichiers sont bien placés dans le répertoire configuré.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setFileImportDialogOpen(false)}>
            Fermer
          </Button>
          <Button 
            variant="outlined" 
            onClick={scanSAADirectory}
            startIcon={<Refresh />}
            disabled={importLoading}
          >
            Actualiser
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <FileImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImportResults}
        availableElements={availableSAAElements}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Batch Analysis Dialog - Optimized for Large Batches */}
      <Dialog 
        open={batchAnalysisDialog} 
        onClose={() => setBatchAnalysisDialog(false)} 
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#1976d2',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1000
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
              <Analytics sx={{ fontSize: '1.8rem' }} />
              <Typography variant="h5" fontWeight="bold">
              Analyse SAA - Lot {selectedBatch?.batchNumber}
            </Typography>
            <Chip 
              label={`${selectedBatch?.samples.length || 0} échantillons`} 
                color="secondary" 
                variant="filled"
                sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}
            />
          </Box>
                      <Button
              onClick={() => setBatchAnalysisDialog(false)}
              sx={{ color: 'white', minWidth: 'auto', p: 1 }}
                      >
              ✕
                      </Button>
                    </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ mt: 1 }}>

            {/* Batch Analysis Results Entry - Optimized Table */}
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    📊 Saisie des Résultats - Lot {selectedBatch?.batchNumber}
                </Typography>
                
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* Import Button for Batch */}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={importLoading ? <CircularProgress size={16} /> : <CloudUpload />}
                      onClick={scanSAADirectoryForBatch}
                      disabled={importLoading}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {importLoading ? 'Scan...' : 'Importer SAA'}
                    </Button>
                
                  {/* Elements Display */}
                  <Box display="flex" gap={1} flexWrap="wrap">
                      {(selectedBatch?.samples?.length ? getBatchSAAElements(selectedBatch.samples) : ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn']).map((element: string) => (
                      <Chip 
                        key={element} 
                        label={element} 
                        size="small"
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                      />
                    ))}
                    </Box>
                  </Box>
                </Box>

                {/* Formula Display */}
                <Alert severity="info" sx={{ mb: 2, py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                    🧮 Formule SAA: <strong>Concentration = L × (V/p) × (vf/vp)</strong> | L = lecture instrument, V = volume initial, p = poids, vf = volume final (par élément), vp = volume prélevé (par élément)
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                    📊 Conversion finale: <strong>% = ppm ÷ 10,000</strong> | Import automatique applique la formule avec vf/vp par élément | <strong>⚗️ Choix individuel: 0 (L×V/p) ou 1 (L×V/p×vf/vp) ou 2 (L×V/p×vf1/vp1)</strong>
                    </Typography>
                  </Alert>

                {/* 🔬 QC Alerts Panel - Displayed after CSV import */}
                {qcAlerts.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Accordion defaultExpanded={qcAlerts.some(a => a.severity === 'CRITICAL' || a.severity === 'WARNING')}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Assessment color={qcAlerts.some(a => a.severity === 'CRITICAL') ? 'error' : qcAlerts.some(a => a.severity === 'WARNING') ? 'warning' : 'success'} />
                          <Typography variant="subtitle1" fontWeight="bold">
                            🔬 Contrôle Qualité (QC) - {qcAlerts.filter(a => a.severity === 'CRITICAL').length} Critiques, {qcAlerts.filter(a => a.severity === 'WARNING').length} Avertissements
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={1}>
                          {/* Group alerts by element */}
                          {Object.entries(
                            qcAlerts.reduce((acc, alert) => {
                              if (!acc[alert.element]) acc[alert.element] = [];
                              acc[alert.element].push(alert);
                              return acc;
                            }, {} as Record<string, QCAlert[]>)
                          ).map(([element, elementAlerts]) => (
                            <Grid item xs={12} sm={6} md={4} key={element}>
                              <Paper 
                                elevation={1} 
                                sx={{ 
                                  p: 1.5, 
                                  borderLeft: `4px solid ${
                                    elementAlerts.some(a => a.severity === 'CRITICAL') ? '#f44336' :
                                    elementAlerts.some(a => a.severity === 'WARNING') ? '#ff9800' : '#4caf50'
                                  }`,
                                  backgroundColor: alpha(
                                    elementAlerts.some(a => a.severity === 'CRITICAL') ? '#f44336' :
                                    elementAlerts.some(a => a.severity === 'WARNING') ? '#ff9800' : '#4caf50',
                                    0.05
                                  )
                                }}
                              >
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                  {element}
                                </Typography>
                                {elementAlerts.map((alert, idx) => (
                                  <Box key={idx} display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.3 }}>
                                    {alert.severity === 'CRITICAL' ? (
                                      <Error fontSize="small" color="error" />
                                    ) : alert.severity === 'WARNING' ? (
                                      <Warning fontSize="small" color="warning" />
                                    ) : alert.severity === 'OK' ? (
                                      <CheckCircle fontSize="small" color="success" />
                                    ) : (
                                      <TipsAndUpdates fontSize="small" color="info" />
                                    )}
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                      {alert.message}
                                    </Typography>
                                  </Box>
                                ))}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                        
                        {/* QC Summary Statistics */}
                        {qcData && qcData.summary && Object.keys(qcData.summary).length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                              📊 Résumé QC par Élément
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Élément</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TQ (n)</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TQ Moy.</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TQ RSD%</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Blancs (n)</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Blanc Moy.</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(qcData.summary).map(([element, summary]) => {
                                    const s = summary as QCElementSummary;
                                    return (
                                      <TableRow key={element}>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{element}</TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{s.tq_count}</TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{s.tq_average?.toFixed(4) || '-'}</TableCell>
                                        <TableCell 
                                          align="center" 
                                          sx={{ 
                                            fontSize: '0.75rem',
                                            color: s.tq_rsd > 5 ? '#f44336' : '#4caf50',
                                            fontWeight: s.tq_rsd > 5 ? 'bold' : 'normal'
                                          }}
                                        >
                                          {s.tq_rsd?.toFixed(1) || '-'}%
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{s.blank_count}</TableCell>
                                        <TableCell 
                                          align="center" 
                                          sx={{ 
                                            fontSize: '0.75rem',
                                            color: Math.abs(s.blank_average || 0) > 0.1 ? '#ff9800' : 'inherit'
                                          }}
                                        >
                                          {s.blank_average?.toFixed(4) || '-'}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}

                {/* Optimized Analysis Results Table */}
                <Box sx={{ 
                  maxHeight: 'calc(100vh - 200px)', 
                  overflow: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: 1
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#1976d2', color: 'white', fontSize: '0.8rem', fontWeight: 'bold', py: 1 } }}>
                        <TableCell rowSpan={2} sx={{ verticalAlign: 'middle', minWidth: '120px' }}>
                          Échantillon
                        </TableCell>
                        
                        {/* Formule SAA Variables - V and p only (sample-specific) */}
                        <TableCell colSpan={2} align="center" sx={{ backgroundColor: '#ff9800', borderLeft: '2px solid #ff6f00' }}>
                          Variables Échantillon
                        </TableCell>
                        
                        {/* 🔥 ELEMENT HEADERS with editable vf/vp (element-level, applies to ALL samples) */}
                        {(selectedBatch?.samples?.length ? getBatchSAAElements(selectedBatch.samples) : ['Fe', 'Cu', 'Pb', 'Zn', 'Ag']).map((element: string) => {
                          const elemParams = elementDilutionParams[element] || { vf: 50, vp: 10, vf1: 25, vp1: 5 };
                          return (
                            <TableCell 
                              key={element} 
                              colSpan={4}
                              align="center" 
                              sx={{ borderLeft: '2px solid #4caf50', minWidth: '280px', p: 0.5 }}
                            >
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                  {element}
                                </Typography>
                                <Box display="flex" gap={0.5} justifyContent="center" alignItems="center" flexWrap="wrap">
                                  <Box display="flex" alignItems="center" gap={0.25}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>vf:</Typography>
                                    <input
                                      type="number"
                                      value={elemParams.vf}
                                      onChange={(e) => {
                                        const newVf = parseFloat(e.target.value) || 1;
                                        setElementDilutionParams(prev => ({
                                          ...prev,
                                          [element]: { ...prev[element], vf: newVf }
                                        }));
                                      }}
                                      style={{ width: '45px', textAlign: 'center', fontSize: '0.7rem', padding: '2px', border: '1px solid #9c27b0', borderRadius: '3px' }}
                                    />
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={0.25}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>vp:</Typography>
                                    <input
                                      type="number"
                                      value={elemParams.vp}
                                      onChange={(e) => {
                                        const newVp = parseFloat(e.target.value) || 1;
                                        setElementDilutionParams(prev => ({
                                          ...prev,
                                          [element]: { ...prev[element], vp: newVp }
                                        }));
                                      }}
                                      style={{ width: '45px', textAlign: 'center', fontSize: '0.7rem', padding: '2px', border: '1px solid #9c27b0', borderRadius: '3px' }}
                                    />
                                  </Box>
                                  <Chip 
                                    label={`Fd=${(elemParams.vf / elemParams.vp).toFixed(1)}`}
                                    size="small"
                                    sx={{ fontSize: '0.6rem', height: '18px', backgroundColor: '#9c27b0', color: 'white' }}
                                  />
                                </Box>
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#1976d2', color: 'white', fontSize: '0.7rem', py: 0.5 } }}>
                        {/* Variables de l'échantillon - V et p seulement */}
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          V (mL)
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          p (g)
                        </TableCell>
                        
                        {/* Résultats pour chaque élément */}
                        {/* 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering */}
                        {(selectedBatch?.samples?.length ? getBatchSAAElements(selectedBatch.samples) : ['Fe', 'Cu', 'Pb', 'Zn', 'Ag']).map((element: string) => (
                          <React.Fragment key={element}>
                            <TableCell align="center" sx={{ borderLeft: '1px solid #4caf50', minWidth: '60px', backgroundColor: '#e8f5e8' }}>
                              L-{element}
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: '70px', backgroundColor: '#e8f5e8' }}>
                              ppm
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: '70px', backgroundColor: '#e3f2fd' }}>
                              %
                            </TableCell>
                            {/* 🔥 FIXED: Added missing dilution choice column header */}
                            <TableCell align="center" sx={{ minWidth: '50px', backgroundColor: '#f3e5f5' }}>
                              Choix
                            </TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBatch?.samples
                        .sort((a, b) => {
                          // Sort samples in increasing order by sample_id
                          const aId = a.sample_id || '';
                          const bId = b.sample_id || '';
                          return aId.localeCompare(bId, undefined, { numeric: true });
                        })
                        .map((sample, sampleIndex) => {
                        const prepData = sample.metadonnees?.saa_preparation;
                        const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                        const elementsToAnalyze = getSAAElementsFromMethod(sample);
                        const allBatchElements = selectedBatch?.samples?.length ? getBatchSAAElements(selectedBatch.samples) : elementsToAnalyze;
                        
                        return (
                          <TableRow 
                            key={sample.id}
                            sx={{ 
                              backgroundColor: sampleIndex % 2 === 0 ? '#fafafa' : 'white',
                              '&:hover': { backgroundColor: '#e3f2fd' },
                              '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8rem' }
                            }}
                          >
                            {/* Sample Info - Compact */}
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                  {sample.sample_id}
                                </Typography>
                            </TableCell>
                            
                            {/* Variables de la formule SAA */}
                            <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                {prepData?.initialVolume?.toFixed(1) || 'N/A'}
                              </Typography>
                            </TableCell>
                            
                            <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                {prepData?.initialWeight?.toFixed(2) || 'N/A'}
                              </Typography>
                            </TableCell>
                            
                            {/* 🔥 vf, vp, vf1, vp1 are now set at element level in the header */}
                            
                            {/* Elements Results - Enhanced with L Reading + Individual Dilution Controls */}
                            {allBatchElements.map((element: string) => {
                              if (!elementsToAnalyze.includes(element)) {
                                return (
                                  <React.Fragment key={element}>
                                    <TableCell sx={{ borderLeft: '1px solid #4caf50', p: 0.5, backgroundColor: '#f5f5f5' }}><Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem', textAlign: 'center' }}>-</Typography></TableCell>
                                    <TableCell sx={{ p: 0.5, backgroundColor: '#f5f5f5' }}><Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem', textAlign: 'center' }}>-</Typography></TableCell>
                                    <TableCell sx={{ p: 0.5, backgroundColor: '#f5f5f5' }}><Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem', textAlign: 'center' }}>-</Typography></TableCell>
                                    <TableCell sx={{ p: 0.5, backgroundColor: '#f5f5f5' }}><Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem', textAlign: 'center' }}>-</Typography></TableCell>
                                  </React.Fragment>
                                );
                              }
                              const elementData = sampleAnalysisData[element] || { ppm: 0, timestamp: '', originalReading: undefined, calculatedFromFormula: false };
                              const ppm = elementData.ppm || 0;
                              const originalReading = elementData.originalReading || 0;
                              
                              // 🔥 FIXED: Get element-specific dilution choice
                              const elementDilutionChoice = elementDilutionChoices[sample.id]?.[element] ?? 1;
                              
                              // Calculate BOTH ppm and percentage from raw lecture value using dilution choice
                              const calculation = calculateFromLecture(originalReading, sample.id, element, prepData);
                              const calculatedPpm = calculation.ppm;
                              const percent = calculation.percent;


                              // Check detection limits using calculated ppm
                              const validation = methodValidations.find(v => v.element === element);
                              const finalPpm = originalReading > 0 ? calculatedPpm : ppm; // Use calculated ppm if we have original reading
                              const belowLOD = validation ? finalPpm < validation.lod_ppm : false;
                              const belowLOQ = validation ? finalPpm >= validation.lod_ppm && finalPpm < validation.loq_ppm : false;
                              
                              return (
                                <React.Fragment key={element}>
                                  {/* L Reading (Instrument Reading) */}
                                  <TableCell sx={{ borderLeft: '1px solid #4caf50', p: 0.5, backgroundColor: '#f1f8e9' }}>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontSize: '0.75rem', 
                                        textAlign: 'center',
                                        fontWeight: elementData.calculatedFromFormula ? 'bold' : 'normal',
                                        color: elementData.calculatedFromFormula ? '#2e7d32' : 'text.primary'
                                      }}
                                    >
                                      {originalReading > 0 ? originalReading.toFixed(4) : '-'}
                                    </Typography>
                                    {elementData.calculatedFromFormula && (
                                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#4caf50', display: 'block' }}>
                                        Import
                                      </Typography>
                                    )}
                                  </TableCell>
                                  
                                  {/* ppm Input/Display - OPTIMIZED Native Input */}
                                  <TableCell sx={{ p: 0.5, backgroundColor: '#f1f8e9' }}>
                                    <NativeNumberInput
                                      value={finalPpm || 0}
                                      onChange={handleNativeInputChange}
                                      sampleId={sample.id}
                                      element={element}
                                      hasValue={finalPpm > 0}
                                    />
                                  </TableCell>
                                  
                                  {/* Calculated % */}
                                  <TableCell sx={{ p: 0.5, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="bold"
                                      color={belowLOD ? 'error' : belowLOQ ? 'warning.main' : 'primary'}
                                      sx={{ fontSize: '0.7rem' }}
                                    >
                                      {belowLOD ? '&lt; LOD' : finalPpm > 0 ? `${percent.toFixed(4)}%` : '-'}
                                    </Typography>
                                      {(elementData.calculatedFromFormula || originalReading > 0) && finalPpm > 0 && (
                                        <Tooltip title={`Calculé avec formule ${elementDilutionChoice === 0 ? 'L × (V/p)' : elementDilutionChoice === 2 ? 'L × (V/p) × (vf1/vp1)' : 'L × (V/p) × (vf/vp)'} | L=${originalReading?.toFixed(4) || 'N/A'}, V=${prepData?.initialVolume || 'N/A'}, p=${prepData?.initialWeight || 'N/A'}${elementDilutionChoice === 2 ? `, vf1=${prepData?.vf1 || 'N/A'}, vp1=${prepData?.vp1 || 'N/A'}` : elementDilutionChoice === 1 ? `, vf=${prepData?.dilutions?.[0]?.finalVolume || 'N/A'}, vp=${prepData?.dilutions?.[0]?.volumeTaken || 'N/A'}` : ''}`}>
                                          <Chip 
                                            label="🧮" 
                                            size="small" 
                                            color="success" 
                                            sx={{ fontSize: '0.6rem', height: '16px', minWidth: '16px' }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </TableCell>
                                  
                                  {/* 🔥 NEW: Individual Element Dilution Choice Control */}
                                  <TableCell sx={{ p: 0.5, backgroundColor: '#f3e5f5' }}>
                                    <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <TextField
                                        size="small"
                                        value={elementDilutionChoice}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          // Only allow 0, 1 or 2
                                          if (value === '0' || value === '1' || value === '2' || value === '') {
                                            const newChoice: 0 | 1 | 2 = value === '0' ? 0 : value === '2' ? 2 : 1;
                                            setElementDilutionChoices(prev => ({
                                              ...prev,
                                              [sample.id]: {
                                                ...prev[sample.id],
                                                [element]: newChoice
                                              }
                                            }));
                                          }
                                        }}
                                        onBlur={(e) => {
                                          // Ensure valid value on blur
                                          const value = e.target.value;
                                          if (value !== '0' && value !== '1' && value !== '2') {
                                            setElementDilutionChoices(prev => ({
                                              ...prev,
                                              [sample.id]: {
                                                ...prev[sample.id],
                                                [element]: 1
                                              }
                                            }));
                                          }
                                        }}
                                        inputProps={{ 
                                          style: { 
                                            textAlign: 'center', 
                                            fontSize: '0.8rem', 
                                            fontWeight: 'bold',
                                            padding: '4px'
                                          },
                                          maxLength: 1
                                        }}
                                        sx={{ 
                                          width: '40px',
                                          '& .MuiOutlinedInput-root': {
                                            backgroundColor: elementDilutionChoice === 0 ? '#fff3e0' : 
                                                            elementDilutionChoice === 2 ? '#f3e5f5' : '#e8f5e8',
                                            '& fieldset': {
                                              borderColor: elementDilutionChoice === 0 ? '#ff9800' : 
                                                          elementDilutionChoice === 2 ? '#9c27b0' : '#4caf50',
                                              borderWidth: '2px'
                                            }
                                          }
                                        }}
                                        placeholder="1"
                                      />
                                        
                                        {/* 🔥 NEW: Copy to All Button */}
                                        <Tooltip title={`Appliquer le choix ${elementDilutionChoice} à tous les échantillons pour ${element}`}>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              if (!selectedBatch) return;
                                              
                                              // Apply this dilution choice to all samples for this element
                                              setElementDilutionChoices(prev => {
                                                const updated = { ...prev };
                                                selectedBatch.samples.forEach(batchSample => {
                                                  if (!updated[batchSample.id]) {
                                                    updated[batchSample.id] = {};
                                                  }
                                                  updated[batchSample.id][element] = elementDilutionChoice;
                                                });
                                                return updated;
                                              });
                                            }}
                                            sx={{ 
                                              width: '20px', 
                                              height: '20px',
                                              color: elementDilutionChoice === 0 ? '#ff9800' : 
                                                     elementDilutionChoice === 2 ? '#9c27b0' : '#4caf50',
                                              '&:hover': {
                                                backgroundColor: elementDilutionChoice === 0 ? '#fff3e0' : 
                                                               elementDilutionChoice === 2 ? '#f3e5f5' : '#e8f5e8',
                                              }
                                            }}
                                          >
                                            <ContentCopy sx={{ fontSize: '12px' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                      
                                      <Typography 
                                        variant="caption" 
                                        display="block" 
                                        sx={{ 
                                          fontSize: '0.55rem', 
                                          fontWeight: 'bold',
                                          color: elementDilutionChoice === 0 ? '#ff6f00' : 
                                                 elementDilutionChoice === 2 ? '#9c27b0' : '#2e7d32',
                                          lineHeight: 0.9
                                        }}
                                      >
                                        {elementDilutionChoice === 0 ? 'L×(V/p)' : 
                                         elementDilutionChoice === 2 ? 'L×(V/p)×(vf1/vp1)' : 'L×(V/p)×(vf/vp)'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                </React.Fragment>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                        </Box>

                {/* Summary Statistics - Compact */}
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32', fontSize: '1rem', fontWeight: 'bold' }}>
                    📊 Résumé du Lot
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Total Échantillons</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>{selectedBatch?.samples.length || 0}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Éléments</Typography>
                      <Typography variant="h6" color="primary.main" sx={{ fontSize: '1rem' }}>
                        {/* 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering */}
                        {selectedBatch?.samples?.length ? getBatchSAAElements(selectedBatch.samples).length : 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Mesures Complétées</Typography>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem' }}>
                        {(() => {
                          let completedCount = 0;
                          let totalRequired = 0;
                          selectedBatch?.samples.forEach(sample => {
                            const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                            // 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering
                            const elementsToAnalyze = getSAAElementsFromMethod(sample);
                            totalRequired += elementsToAnalyze.length;
                            elementsToAnalyze.forEach((element: string) => {
                              const elementData = sampleAnalysisData[element];
                              if (elementData && typeof elementData.ppm === 'number') {
                                completedCount++;
                              }
                            });
                          });
                          return `${completedCount}/${totalRequired}`;
                        })()}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Formule</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', color: '#1976d2' }}>
                        % = ppm ÷ 10,000
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f8f9fa', borderTop: '1px solid #ddd', position: 'sticky', bottom: 0 }}>
          <Button 
            onClick={() => setBatchAnalysisDialog(false)}
            size="large"
            sx={{ fontSize: '1rem' }}
          >
            Annuler
          </Button>
          

          
          <Button 
            variant="contained" 
            color="primary"
            size="large"
            onClick={() => {
              // Validate that all results are entered before completion
              if (selectedBatch) {
                let completedCount = 0;
                let totalRequired = 0;
                
                selectedBatch.samples.forEach(sample => {
                  const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                  // 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering
                  const elementsToAnalyze = getSAAElementsFromMethod(sample);
                  elementsToAnalyze.forEach((element: string) => {
                    totalRequired++;
                    const elementData = sampleAnalysisData[element];
                    if (elementData && typeof elementData.ppm === 'number') {
                      completedCount++;
                    }
                  });
                });
                
                if (completedCount < totalRequired) {
                  alert(`Veuillez compléter toutes les mesures ppm. ${completedCount}/${totalRequired} mesures saisies.`);
                  return;
                }
                
                handleCompleteBatchAnalysis();
              }
            }}
            startIcon={<Analytics />}
            disabled={(() => {
              if (!selectedBatch) return true;
              
              let completedCount = 0;
              let totalRequired = 0;
              
              selectedBatch.samples.forEach(sample => {
                const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                // 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering
                const elementsToAnalyze = getSAAElementsFromMethod(sample);
                elementsToAnalyze.forEach((element: string) => {
                  totalRequired++;
                  const elementData = sampleAnalysisData[element];
                  if (elementData && typeof elementData.ppm === 'number') {
                    completedCount++;
                  }
                });
              });
              
              return completedCount < totalRequired;
            })()}
            sx={{ fontSize: '1rem', px: 3 }}
          >
            🧪 Finaliser Analyse du Lot ({(() => {
              if (!selectedBatch) return '0/0';
              
              let completedCount = 0;
              let totalRequired = 0;
              
              selectedBatch.samples.forEach(sample => {
                const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                // 🔥 FIX: Use getSAAElementsFromMethod for multi-lab filtering
                const elementsToAnalyze = getSAAElementsFromMethod(sample);
                elementsToAnalyze.forEach((element: string) => {
                  totalRequired++;
                  const elementData = sampleAnalysisData[element];
                  if (elementData && typeof elementData.ppm === 'number') {
                    completedCount++;
                  }
                });
              });
              
              return `${completedCount}/${totalRequired}`;
            })()})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

// Helper function to generate CSV template
const generateCSVTemplate = (): string => {
  const headers = [
    'Sample_ID',
    'Lab_Ref', 
    'Customer_Ref',
    'Lab_Weight',
    'Dilution_Factor',
    'Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'
  ];
  
  const sampleRows = [
    ['AFL-2025-0001', 'LAB001', 'CLIENT-001', '2.5', '250', '0.05', '0.02', '0.15', '0.85', '35.2', '0.45', '0.12', '0.25', '1.85'],
    ['AFL-2025-0002', 'LAB002', 'CLIENT-002', '2.3', '250', '0.08', '0.01', '0.18', '1.25', '42.1', '0.52', '0.18', '0.35', '2.15']
  ];

  return [headers.join(','), ...sampleRows.map(row => row.join(','))].join('\n');
};

export default SAAAnalystDashboard; 